/* ==========================================================
   Shary Web V1 — Single JS Runtime (assets/shary-v1.js)
   Includes: Phase 0–12 (merged)
   RULE: one runtime file. No duplicated logic.
   ========================================================== */

/* -------------------------
   Global Shary Namespace + Core Helpers
   Must be defined FIRST so all IIFEs/modules can access them.
   ------------------------- */
window.Shary = window.Shary || {};

window.Shary.readBool = function(v, fallback) {
  if (fallback === undefined) fallback = false;
  if (v == null) return fallback;
  var s = String(v).trim().toLowerCase();
  if (!s) return fallback;
  return s === "true" || s === "1" || s === "yes" || s === "on";
};

window.Shary.readStr = function(v) {
  return (v == null ? "" : String(v)).trim();
};

window.Shary.readInt = function(v, fallback) {
  if (fallback === undefined) fallback = 0;
  var n = parseInt(String(v || ""), 10);
  return Number.isFinite(n) ? n : fallback;
};

window.Shary.isLoginPage = function() {
  try {
    var cl = document.body && document.body.classList;
    if (cl) {
      if (
        cl.contains("template-customers-login") ||
        cl.contains("template-customers-register") ||
        cl.contains("template-customers-account")
      ) {
        return true;
      }
    }
    var path = (location.pathname || "").toLowerCase();
    if (path === "/account" || path === "/account/") return true;
    if (path.startsWith("/account/login") || path.startsWith("/account/register")) return true;
    return false;
  } catch (_) {
    return false;
  }
};

/* -------------------------
   Debug helper (reads window.SHARY_DEBUG set by theme.liquid)
   ------------------------- */
const sharyLog = (...args) => {
  if (window.SHARY_DEBUG) console.log("[SHARY]", ...args);
};

window.__SHARY_DEBUG_QTY = false;

const sharyStorage = (() => {
  const support = { localStorage: null, sessionStorage: null };
  const warned = { localStorage: false, sessionStorage: false };

  const debugEnabled = () => window.__SHARY_DEBUG === true;

  const warnOnce = (type, err) => {
    if (!debugEnabled() || warned[type]) return;
    warned[type] = true;
    const msg = err && err.message ? err.message : String(err || "blocked");
    console.warn("[SHARY] Storage blocked:", type, msg);
  };

  const canUse = (type) => {
    if (support[type] !== null) return support[type];
    try {
      const store = window[type];
      if (!store) {
        support[type] = false;
        return false;
      }
      const testKey = "__shary_test__";
      store.setItem(testKey, testKey);
      store.removeItem(testKey);
      support[type] = true;
      return true;
    } catch (err) {
      support[type] = false;
      warnOnce(type, err);
      return false;
    }
  };

  const withStore = (type, fn) => {
    if (!canUse(type)) return null;
    try {
      return fn(window[type]);
    } catch (err) {
      warnOnce(type, err);
      return null;
    }
  };

  const makeApi = (type) => ({
    get: (key) => withStore(type, (store) => store.getItem(key)),
    set: (key, value) => withStore(type, (store) => {
      store.setItem(key, value);
      return true;
    }),
    remove: (key) => withStore(type, (store) => {
      store.removeItem(key);
      return true;
    }),
  });

  return {
    local: makeApi("localStorage"),
    session: makeApi("sessionStorage"),
  };
})();

const sharyBuildWhatsAppUrl = (phone, message) => {
  const safePhone = String(phone || "").replace(/[^\d]/g, "");
  if (!safePhone) return null;
  const safeMessage = message == null ? "" : String(message);
  return `https://wa.me/${safePhone}?text=${encodeURIComponent(safeMessage)}`;
};

/* -------------------------
   Page Context Detection
   Used to skip qty/interaction handlers on login/register/account pages.
   Returns true ONLY for auth-related pages, not general account pages.
   NOTE: Wraps window.Shary.isLoginPage for backward compatibility.
   ------------------------- */
function isLoginPage() {
  return window.Shary.isLoginPage();
}

/* -------------------------
   Performance Instrumentation (disabled by default)
   Enable with: window.__SHARY_PERF_DEBUG = true
   View stats: sharyPerf.report() in console
   ------------------------- */
const sharyPerf = (() => {
  const enabled = () => window.__SHARY_PERF_DEBUG === true;
  const counters = {
    cartFetch: 0,
    cartFetchCacheHit: 0,
    cartFetchDedupe: 0,
    cartAdd: 0,
    cartChange: 0,
    listeners: 0,
    observerTriggers: 0,
    domQueries: 0,
    renderCycles: 0,
    qtyStepperUpdates: 0
  };
  const timings = [];
  const MAX_TIMINGS = 100;

  // Track listener registrations
  const listenerMap = new Map(); // element -> Set of event types

  const trackListener = (element, eventType) => {
    if (!enabled()) return;
    counters.listeners++;
    const key = element || 'document';
    if (!listenerMap.has(key)) listenerMap.set(key, new Set());
    listenerMap.get(key).add(eventType);
  };

  const recordTiming = (name, durationMs) => {
    if (!enabled()) return;
    timings.push({ name, duration: durationMs, timestamp: Date.now() });
    if (timings.length > MAX_TIMINGS) timings.shift();
  };

  return {
    enabled,
    mark: (name) => enabled() && performance.mark(`shary:${name}`),
    measure: (name, start, end) => {
      if (!enabled()) return;
      try {
        performance.measure(`shary:${name}`, `shary:${start}`, `shary:${end}`);
        const entries = performance.getEntriesByName(`shary:${name}`);
        if (entries.length) recordTiming(name, entries[entries.length - 1].duration);
      } catch (_) {}
    },
    count: (key) => { if (enabled()) counters[key] = (counters[key] || 0) + 1; },
    log: (msg, data) => enabled() && console.log(`[SHARY PERF] ${msg}`, data || ''),
    getCounters: () => ({ ...counters }),
    getTimings: () => [...timings],
    trackListener,
    time: (label) => enabled() && console.time(`[SHARY] ${label}`),
    timeEnd: (label) => enabled() && console.timeEnd(`[SHARY] ${label}`),

    // Performance report - call sharyPerf.report() in console
    report: () => {
      const c = counters;
      const cacheHitRate = c.cartFetch > 0 ? ((c.cartFetchCacheHit / c.cartFetch) * 100).toFixed(1) : 0;
      const dedupeRate = c.cartFetch > 0 ? ((c.cartFetchDedupe / c.cartFetch) * 100).toFixed(1) : 0;

      console.group('%c[SHARY PERF REPORT]', 'color: #F47C20; font-weight: bold; font-size: 14px;');

      console.log('%cCart Operations:', 'font-weight: bold;');
      console.table({
        'Total Fetch Requests': c.cartFetch,
        'Cache Hits': c.cartFetchCacheHit,
        'Cache Hit Rate': `${cacheHitRate}%`,
        'Deduplicated': c.cartFetchDedupe,
        'Dedupe Rate': `${dedupeRate}%`,
        'Add Calls': c.cartAdd,
        'Change Calls': c.cartChange
      });

      console.log('%cUI Operations:', 'font-weight: bold;');
      console.table({
        'Event Listeners': c.listeners,
        'Observer Triggers': c.observerTriggers,
        'DOM Queries': c.domQueries,
        'Render Cycles': c.renderCycles,
        'Qty Stepper Updates': c.qtyStepperUpdates
      });

      if (timings.length > 0) {
        console.log('%cRecent Timings (ms):', 'font-weight: bold;');
        const avgByName = {};
        timings.forEach((t) => {
          if (!t || !t.name) return;
          if (!avgByName[t.name]) avgByName[t.name] = { total: 0, count: 0 };
          avgByName[t.name].total += t.duration || 0;
          avgByName[t.name].count += 1;
        });
        const avgRows = {};
        Object.keys(avgByName).forEach((name) => {
          const row = avgByName[name];
          avgRows[name] = row.count ? (row.total / row.count).toFixed(2) : "0";
        });
        console.table(avgRows);
      }

      console.groupEnd();
    }
  };
})();

/* =========================
   Cart Store — centralized cache + dedupe for /cart.js
   ========================= */
const sharyCartStore = (() => {
  let cache = null;
  let cacheAt = 0;
  let pending = null;
  const TTL_MS = 1500;

  const fetchCart = async (forceFresh = false) => {
    const nowTs = Date.now();
    if (!forceFresh && cache && (nowTs - cacheAt) < TTL_MS) {
      if (typeof sharyPerf !== "undefined") sharyPerf.count("cartFetchCacheHit");
      return cache;
    }
    if (pending) {
      if (typeof sharyPerf !== "undefined") sharyPerf.count("cartFetchDedupe");
      return pending;
    }

    if (typeof sharyPerf !== "undefined") sharyPerf.count("cartFetch");

    pending = fetch("/cart.js", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((res) => {
        if (!res.ok) throw new Error("cart_fetch_failed");
        return res.json();
      })
      .then((data) => {
        cache = data || null;
        cacheAt = Date.now();
        return cache;
      })
      .finally(() => {
        pending = null;
      });

    return pending;
  };

  const getCache = () => cache;

  const invalidate = () => {
    cache = null;
    cacheAt = 0;
  };

  return { fetchCart, getCache, invalidate };
})();

/* =========================
   Phase 1 — Cart Badge Sync (CENTRALIZED)
   Single listener for ALL cart badges across topbar and bottom nav.
   Listens to shary:cart-updated and shary:cart-changed events.
   ========================= */
(() => {
  const updateAllBadges = async () => {
    try {
      const cart = await sharyCartStore.fetchCart();
      const count = cart && cart.item_count ? cart.item_count : 0;
      const badges = document.querySelectorAll("[data-shary-cart-badge]");
      badges.forEach((badge) => {
        const prev = parseInt(badge.textContent, 10) || 0;
        badge.textContent = String(count);
        badge.hidden = count === 0;
        // Pop animation on increase
        if (count > prev && count > 0) {
          badge.removeAttribute("data-pop");
          void badge.offsetWidth; // Force reflow
          badge.setAttribute("data-pop", "");
        }
      });
    } catch (err) {
      if (window.SHARY_DEBUG) console.warn("[SHARY] Badge sync error:", err);
    }
  };

  // Listen for cart events
  window.addEventListener("shary:cart-updated", updateAllBadges);
  window.addEventListener("shary:cart-changed", updateAllBadges);

  // Initial sync on page load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", updateAllBadges);
  } else {
    updateAllBadges();
  }
})();

/* =========================
   Phase 9.2.2 — Account Recommendations (Account Page)
   Settings-driven data source with empty/loading states
   ========================= */
(() => {
  try {
    const root = document.querySelector("[data-shary-account]");
    if (!root) return;

    const grid = root.querySelector("[data-shary-account-grid]");
    const loading = root.querySelector("[data-shary-account-loading]");
    const empty = root.querySelector("[data-shary-account-empty]");
    if (!grid) return;

    const recoEnabled = (root.getAttribute("data-reco-enabled") || "true") === "true";
    if (!recoEnabled) {
      if (loading) loading.hidden = true;
      if (empty) empty.hidden = true;
      return;
    }

    const source = (root.getAttribute("data-reco-source") || "reorder_snapshot").trim();
    const maxRaw = parseInt(root.getAttribute("data-reco-max") || "4", 10);
    const maxItems = Math.max(1, Math.min(isNaN(maxRaw) ? 4 : maxRaw, 12));
    const fallbackHandle = (root.getAttribute("data-fallback-handle") || "").trim();
    const fallbackUrl = (root.getAttribute("data-fallback-url") || "").trim();

    const body = document.body;
    const customerId = body?.getAttribute("data-shary-customer-id") || "0";
    const gateText = (body?.getAttribute("data-shary-gate-price") || "سجل لرؤية الأسعار").trim();
    const isApproved = () => (parseInt(body?.getAttribute("data-shary-auth") || "0", 10) || 0) > 0;

    let initStarted = false;
    let fallbackPromise = null;

    const escapeHtml = (str) => {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    const setState = (state) => {
      if (loading) loading.hidden = state !== "loading";
      if (empty) empty.hidden = state !== "empty";
      if (grid) grid.hidden = state === "empty" || state === "loading";
    };

    const getItemKey = (item) => {
      return item.product_id || item.productId || item.handle || item.product_handle || item.url || item.title || "";
    };

    const getUnitPrice = (item) => {
      const qty = item.qty || item.quantity || 1;
      if (item.price != null) return item.price;
      if (item.final_price != null) return item.final_price;
      if (item.line_price != null && qty > 0) return Math.round(item.line_price / qty);
      if (item.final_line_price != null && qty > 0) return Math.round(item.final_line_price / qty);
      return null;
    };

    const buildCardHtml = (item) => {
      const title = escapeHtml(item.product_title || item.title || "منتج");
      const handle = item.handle || item.product_handle || "";
      const url = item.url || item.product_url || (handle ? `/products/${encodeURIComponent(handle)}` : "");
      const imgSrc = item.image_url || item.image || item.featured_image?.url || "";
      const priceCents = getUnitPrice(item);
      const showPrice = isApproved();
      const priceLabel = showPrice
        ? (priceCents != null ? sharyWaMoneyFormat(priceCents) : "—")
        : gateText;

      return `
        <article
          class="shary-card"
          data-shary-card
          data-product-id="${escapeHtml(item.product_id || "") }"
          data-variant-id="${escapeHtml(item.variant_id || "") }"
          data-available="1"
          data-title="${title}"
          data-pack=""
          data-url="${escapeHtml(url)}"
          data-img="${escapeHtml(imgSrc)}"
          data-price-cents="${priceCents != null ? escapeHtml(priceCents) : ""}"
        >
          <div class="shary-card__media-wrap">
            <a class="shary-card__media" href="${escapeHtml(url)}" aria-label="${title}">
              ${imgSrc
                ? `<img class="shary-card__img" src="${escapeHtml(imgSrc)}" alt="${title}" loading="lazy" width="600" height="600">`
                : `<div class="shary-card__img shary-card__img--empty" aria-hidden="true"><div class="shary-account__img-empty"><svg viewBox="0 0 24 24" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 7h16v10H4z" stroke="currentColor" stroke-width="1.6"/><path d="M7 11l2.5 2.5L13 9l4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></div></div>`
              }
              <div class="shary-card__overlay" aria-hidden="true"></div>
            </a>
          </div>
          <div class="shary-card__body">
            <a class="shary-card__title" href="${escapeHtml(url)}">${title}</a>
            <div class="shary-card__meta">
              <span class="shary-card__pack shary-card__pack--empty" aria-hidden="true">&nbsp;</span>
            </div>
            <div class="shary-card__price">
              <span>${escapeHtml(priceLabel)}</span>
            </div>
          </div>
        </article>
      `;
    };

    const fetchLedgerSnapshots = async (limit) => {
      const fetchUrl = `/apps/shary-ledger/list?limit=${limit}`;
      try {
        const data = await sharyLedgerListStore.fetchList(fetchUrl);
        if (!data || data.ok !== true || !Array.isArray(data.snapshots)) return null;
        return data.snapshots.map((snap) => sharyNormalizeSnapshot(snap));
      } catch (_) {
        return null;
      }
    };

    const pickLedgerItems = (snapshots, limit) => {
      const picked = [];
      const seen = new Set();
      for (const snap of snapshots || []) {
        for (const item of snap.items || []) {
          const hasLink = item.url || item.product_url || item.handle || item.product_handle;
          if (!hasLink) continue;
          const key = String(getItemKey(item) || "");
          if (!key || seen.has(key)) continue;
          seen.add(key);
          picked.push(item);
          if (picked.length >= limit) return picked;
        }
      }
      return picked;
    };

    const pickLocalSnapshotItems = (limit) => {
      if (typeof sharyReorderLoad !== "function") return [];
      const now = Date.now();
      let list = sharyReorderLoad();
      if (typeof sharyReorderPrune === "function") {
        list = sharyReorderPrune(list, now);
      }
      list.sort((a, b) => (Number(b.lastSeenAt) || 0) - (Number(a.lastSeenAt) || 0));

      const picked = [];
      const seen = new Set();
      for (const item of list) {
        const key = String(item.variant_id || item.product_id || item.handle || item.title || "");
        if (!key || seen.has(key)) continue;
        seen.add(key);
        picked.push(item);
        if (picked.length >= limit) break;
      }
      return picked;
    };

    const fetchFallbackCards = async (limit) => {
      if (!fallbackHandle && !fallbackUrl) return [];
      if (fallbackPromise) return fallbackPromise;

      const baseUrl = fallbackUrl || `/collections/${encodeURIComponent(fallbackHandle)}`;
      const requestUrl = `${baseUrl}?sections=shary-listing`;

      fallbackPromise = fetch(requestUrl, { credentials: "same-origin" })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          const html = data && data["shary-listing"] ? data["shary-listing"] : "";
          if (!html) return [];
          const temp = document.createElement("div");
          temp.innerHTML = html;
          return Array.from(temp.querySelectorAll("[data-shary-card]")).slice(0, limit).map((card) => card.outerHTML);
        })
        .catch(() => []);

      return fallbackPromise;
    };

    const clearLoading = () => {
      if (loading && loading.parentNode) loading.parentNode.removeChild(loading);
    };

    const renderCards = (cardsHtml) => {
      if (!cardsHtml || cardsHtml.length === 0) {
        grid.innerHTML = "";
        setState("ready");
        return;
      }
      grid.innerHTML = cardsHtml.join("");
      setState("ready");
    };

    const init = async () => {
      setState("loading");

      try {
        if (!customerId || customerId === "0") {
          grid.innerHTML = "";
          setState("ready");
          return;
        }

        let cards = [];

        if (source === "reorder_snapshot") {
          const snapshots = await fetchLedgerSnapshots(3);
          if (snapshots && snapshots.length > 0) {
            cards = pickLedgerItems(snapshots, maxItems).map(buildCardHtml);
          }
          if (cards.length === 0) {
            const localItems = pickLocalSnapshotItems(maxItems);
            cards = localItems.map(buildCardHtml);
          }
        } else if (source === "last_order_from_cart_snapshot") {
          const localItems = pickLocalSnapshotItems(maxItems);
          cards = localItems.map(buildCardHtml);
        }

        if (cards.length === 0 && source !== "fallback_collection") {
          const fallbackCards = await fetchFallbackCards(maxItems);
          if (fallbackCards.length > 0) {
            renderCards(fallbackCards);
            return;
          }
        }

        if (cards.length === 0 && source === "fallback_collection") {
          const fallbackCards = await fetchFallbackCards(maxItems);
          renderCards(fallbackCards);
          return;
        }

        renderCards(cards);
      } catch (_) {
        grid.innerHTML = "";
        setState("ready");
      } finally {
        clearLoading();
      }
    };

    const start = () => {
      if (initStarted) return;
      initStarted = true;
      init();
    };

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          start();
        }
      }, { rootMargin: "200px 0px" });
      observer.observe(root);
    } else {
      requestAnimationFrame(start);
    }
  } catch (_) {}
})();

/* -------------------------
   Phase 1.1 — Back to Top + Shortcuts auto-scroll
   ------------------------- */
(() => {
  try {
    const BACKTOTOP_THRESHOLD = 600;
    const SHORTCUT_RESUME_DELAY_MS = 600;
    const GLOBAL_DIR = document.documentElement.getAttribute("dir") || "";

    const btn = document.querySelector("[data-shary-backtotop]");
    const setupBackToTop = (el) => {
      let lastVisible = null;
      const toggle = () => {
        const y = window.scrollY || 0;
        const shouldShow = y > BACKTOTOP_THRESHOLD;
        if (shouldShow === lastVisible) return; // Skip if no change
        lastVisible = shouldShow;
        if (shouldShow) el.setAttribute("data-visible", "true");
        else el.removeAttribute("data-visible");
      };
      // Throttle scroll handler to 100ms for performance
      window.addEventListener("scroll", sharyThrottle(toggle, 100), { passive: true });
      el.addEventListener("click", () => window.scrollTo({ top: 0, behavior: "smooth" }));
      toggle();
    };

    const setupShortcutRow = (row, durationMs) => {
      const direction = row.getAttribute("dir") || GLOBAL_DIR || getComputedStyle(row).direction;
      const isRtl = direction === "rtl";
      const normalize = (v) => (isRtl ? -v : v);
      const max = Math.max(0, row.scrollWidth - row.clientWidth);
      if (max <= 0) return;
      let dir = 1;
      let paused = false;
      let timer = null;
      const sweep = () => {
        if (paused) return;
        const target = dir > 0 ? max : 0;
        row.scrollTo({ left: normalize(target), behavior: "smooth" });
        dir *= -1;
        timer = window.setTimeout(sweep, durationMs);
      };
      const clear = () => { if (timer) window.clearTimeout(timer); };
      row.addEventListener("pointerenter", () => { paused = true; clear(); });
      row.addEventListener("pointerleave", () => { clear(); paused = false; timer = window.setTimeout(sweep, SHORTCUT_RESUME_DELAY_MS); });
      row.addEventListener("touchstart", () => { paused = true; clear(); }, { passive: true });
      row.addEventListener("touchend", () => { paused = false; timer = window.setTimeout(sweep, SHORTCUT_RESUME_DELAY_MS); }, { passive: true });
      timer = window.setTimeout(sweep, SHORTCUT_RESUME_DELAY_MS);
    };

    if (btn) setupBackToTop(btn);

    const rows = Array.from(document.querySelectorAll(".shary-chiprow__scroll"));
    if (rows.length) {
      const val = getComputedStyle(document.documentElement).getPropertyValue("--shary-shortcuts-speed");
      const n = parseFloat(String(val || "").replace("s", "").trim());
      const durationMs = (Number.isFinite(n) && n > 0 ? n : 24) * 1000;
      rows.forEach((row) => setupShortcutRow(row, durationMs));
    }
  } catch (e) {}
})();

/* -------------------------
   Phase 1.2 — Liquid Glass Header Shrink on Scroll
   Toggles data-shary-glass-shrink attribute on topbar
   Uses rAF + passive scroll listener for performance
   Respects Reduce Motion preference
   ------------------------- */
(() => {
  try {
    const body = document.body;
    if (!body) return;

    // Only activate if glass header shrink is enabled
    const shrinkEnabled = body.dataset.sharyGlassHeaderShrink === "true";
    const reduceMotion = body.dataset.sharyReduceMotion === "true" ||
                         window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!shrinkEnabled || reduceMotion) return;

    const topbar = document.querySelector(".shary-topbar");
    if (!topbar) return;

    const SHRINK_THRESHOLD = 40;
    let isShrunk = false;
    let ticking = false;

    const updateShrinkState = () => {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const shouldShrink = scrollY > SHRINK_THRESHOLD;

      if (shouldShrink !== isShrunk) {
        isShrunk = shouldShrink;
        if (isShrunk) {
          topbar.setAttribute("data-shary-glass-shrink", "true");
        } else {
          topbar.removeAttribute("data-shary-glass-shrink");
        }
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateShrinkState);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    // Initial state check
    updateShrinkState();

    sharyLog("Phase 1.2: Glass header shrink enabled");
  } catch (e) {
    console.warn("[SHARY V1] Glass header shrink error:", e);
  }
})();

/* -------------------------
   Phase 2 — WhatsApp (build URL) — FIX: handle ALL links
   ------------------------- */
(() => {
  try {
    const links = document.querySelectorAll("[data-shary-wa]");
    if (!links || links.length === 0) return;

    links.forEach((a) => {
      const rawPhone = (a.getAttribute("data-wa-phone") || "").trim();
      const message = (a.getAttribute("data-wa-message") || "").trim();

      const phone = rawPhone.replace(/[^\d]/g, "");
      if (!phone) return;

      const url = sharyBuildWhatsAppUrl(phone, message);
      if (!url) return;
      a.setAttribute("href", url);
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
  } catch (e) {}
})();

/* -------------------------
   Phase 3 — Auth Gate + Intent Restore (Core mechanics) (LOCKED)
   ------------------------- */
(() => {
  const LS_KEY = "shary_intent_v1";
  const INTENT_TTL_MS = 20 * 60 * 1000; // 20 minutes

  const body = document.body;
  if (!body) return;

  // Use global helpers from window.Shary namespace
  const readBool = window.Shary.readBool;
  const readStr = window.Shary.readStr;
  const readInt = window.Shary.readInt;

  const sanitizeLoginUrl = (raw) => {
    const url = readStr(raw) || "/account/login";
    let normalized = url.toLowerCase();
    try {
      normalized = decodeURIComponent(normalized);
    } catch (_) {}
    if (normalized.includes("shopify.com/authentication") || normalized.includes("pages/account/login")) return "/account/login";
    if (normalized === "/account/login/") return "/account/login";
    return url;
  };

  const buildLoginUrl = (returnPath) => {
    const base = sanitizeLoginUrl(body.getAttribute("data-shary-account-login-url"));
    const localeQ = readStr(body.getAttribute("data-shary-accounts-locale-query")) || "locale=ar&region_country=EG";
    const safeLocale = localeQ.replace(/^[?&]+/, "");
    const returnUrl = returnPath ? encodeURIComponent(returnPath) : null;

    let target = base;

    if (returnUrl) {
      target += (target.includes("?") ? "&" : "?") + `return_url=${returnUrl}`;
    }

    if (safeLocale) {
      const existingQuery = target.includes("?") ? target.split("?")[1] : "";
      const existingParams = new URLSearchParams(existingQuery);
      const localeParams = new URLSearchParams(safeLocale);
      const hasLocaleParam = Array.from(localeParams.keys()).some((key) => existingParams.has(key));

      if (!hasLocaleParam) {
        target += (target.includes("?") ? "&" : "?") + safeLocale;
      }
    }

    return target;
  };

  // If attribute missing, default TRUE (strict gating)
  const gateEnabledAttr = body.getAttribute("data-shary-gate-enabled");
  const gateEnabled = gateEnabledAttr == null ? true : readBool(gateEnabledAttr);

  const settings = {
    authId: readInt(body.getAttribute("data-shary-auth"), 0),

    gateEnabled,
    gateTitle: readStr(body.getAttribute("data-shary-gate-title")) || "سجل لإكمال الطلب",
    gateBody:
      readStr(body.getAttribute("data-shary-gate-body")) ||
      "سجل بحسابك لإكمال الطلب. إذا واجهت مشكلة، اطلب عبر واتساب.",
    gateCtaLogin: readStr(body.getAttribute("data-shary-gate-cta-login")) || "تسجيل الدخول",
    gateCtaWa: readStr(body.getAttribute("data-shary-gate-cta-wa")) || "ابعت الطلب واتساب",

    waEnabled: readBool(body.getAttribute("data-shary-wa-enabled")),
    waPhone: readStr(body.getAttribute("data-shary-wa-phone")),
    waMessage: readStr(body.getAttribute("data-shary-wa-message")),
  };

  const isAuthed = () => settings.authId > 0;

  const buildWaUrl = (message) => {
    if (!settings.waEnabled) return null;
    const phone = (settings.waPhone || "").replace(/[^\d]/g, "");
    const msg = (message || settings.waMessage || "").trim();
    if (!phone) return null;
    return sharyBuildWhatsAppUrl(phone, msg);
  };

  /**
   * isSafeIntentUrl — Validates intent restore URLs to prevent OTP redirects.
   * Blocks: shopify.com, /authentication/, /pages/account, external domains.
   * Allows: relative paths (/) or same-origin absolute URLs only.
   */
  const isSafeIntentUrl = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== "string") return false;

    // Block protocol-relative URLs (e.g. //shopify.com/...)
    if (rawUrl.startsWith("//")) return false;

    // Decode up to twice to catch encoded attacks (e.g. %2F → /)
    let decoded = rawUrl;
    try {
      decoded = decodeURIComponent(decoded);
      decoded = decodeURIComponent(decoded);
    } catch (_) {}

    const lower = decoded.toLowerCase();

    // Block known OTP / bad patterns
    const blocklist = [
      "shopify.com",
      "/authentication/",  // trailing slash required to avoid false positives
      "/pages/account",
    ];
    if (blocklist.some((b) => lower.includes(b))) return false;

    // Allow site-relative paths starting with /
    if (rawUrl.startsWith("/")) return true;

    // Allow same-origin absolute URLs only
    try {
      const parsed = new URL(rawUrl, window.location.origin);
      if (parsed.origin === window.location.origin) return true;
    } catch (_) {}

    // Disallow any other external absolute URLs
    return false;
  };

  const now = () => Date.now();

  const saveIntent = (intent) => {
    try {
      const payload = {
        v: 1,
        t: now(),
        url: location.href,
        scrollY: window.scrollY || 0,
        qpOpen: !!intent.qpOpen,
        qpProductId: intent.qpProductId || null,
        productId: intent.productId || null,
        variantId: intent.variantId || null,
        qty: Math.max(1, parseInt(intent.qty || 1, 10)),
      };
      sharyStorage.local.set(LS_KEY, JSON.stringify(payload));
    } catch (e) {}
  };

  const readIntent = () => {
    try {
      const raw = sharyStorage.local.get(LS_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || data.v !== 1) return null;

      if (typeof data.t === "number" && now() - data.t > INTENT_TTL_MS) {
        sharyStorage.local.remove(LS_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  };

  const clearIntent = () => {
    sharyStorage.local.remove(LS_KEY);
  };

  // تم إزالة phone-login — يستخدم Shopify login الأصلي فقط
  const redirectToLogin = () => {
    const returnUrl = location.pathname + location.search + location.hash;
    location.href = buildLoginUrl(returnUrl);
  };

  // Focus trap cleanup and opener for Auth Gate
  let gateTrapCleanup = null;
  let gateOpener = null;

  const closeGate = () => {
    const g = document.getElementById("sharyGate");
    if (g) g.removeAttribute("data-open");
    // Cleanup focus trap
    if (gateTrapCleanup) { gateTrapCleanup(); gateTrapCleanup = null; }
    // Restore focus to opener
    if (gateOpener && typeof gateOpener.focus === "function") {
      try { gateOpener.focus(); } catch (_) {}
    }
    gateOpener = null;
  };

  const buildGate = () => {
    if (document.getElementById("sharyGate")) return;

    const wrap = document.createElement("div");
    wrap.id = "sharyGate";
    wrap.setAttribute("role", "dialog");
    wrap.setAttribute("aria-modal", "true");
    wrap.setAttribute("aria-label", "بوابة تسجيل الدخول");

    const wa = buildWaUrl(settings.waMessage);

    wrap.innerHTML = `
      <div class="sharyGate__backdrop" data-gate-close="1"></div>
      <div class="sharyGate__sheet">
        <button class="sharyGate__close" type="button" aria-label="إغلاق" data-gate-close="1">✕</button>
        <p class="sharyGate__title"></p>
        <p class="sharyGate__body"></p>
        <div class="sharyGate__actions">
          <a class="sharyGate__btn sharyGate__btn--primary" href="#" data-gate-login="1"></a>
          <a class="sharyGate__btn sharyGate__btn--secondary" href="#" data-gate-apply="1"></a>
          ${wa ? `<a class="sharyGate__btn" href="${wa}" target="_blank" rel="noopener noreferrer" data-gate-wa="1"></a>` : ``}
        </div>
      </div>
    `;

    document.body.appendChild(wrap);

    wrap.querySelector(".sharyGate__title").textContent = settings.gateTitle;
    wrap.querySelector(".sharyGate__body").textContent = settings.gateBody;

    const loginBtn = wrap.querySelector("[data-gate-login]");
    if (loginBtn) {
      loginBtn.textContent = settings.gateCtaLogin;
      loginBtn.href = buildLoginUrl(location.pathname + location.search + location.hash);
    }

    const applyBtn = wrap.querySelector("[data-gate-apply]");
    if (applyBtn) {
      const applyLabel = body.getAttribute("data-shary-gate-cta-apply") || "طلب تفعيل الحساب";
      const applyUrl = body.getAttribute("data-shary-b2b-register-url") || "/pages/b2b-apply";
      applyBtn.textContent = applyLabel;
      applyBtn.href = applyUrl;
    }

    const waBtn = wrap.querySelector("[data-gate-wa]");
    if (waBtn) waBtn.textContent = settings.gateCtaWa;

    wrap.addEventListener("click", (e) => {
      if (e.target.closest("[data-gate-close]")) closeGate();
    });

    if (loginBtn) {
      loginBtn.addEventListener("click", (e) => {
        e.preventDefault();
        redirectToLogin();
      });
    }
  };

  const openGate = () => {
    // Store opener for focus restore on close
    gateOpener = document.activeElement;
    buildGate();
    const g = document.getElementById("sharyGate");
    if (g) {
      g.setAttribute("data-open", "true");
      // Enable focus trap on the sheet inside gate
      const sheet = g.querySelector(".sharyGate__sheet");
      gateTrapCleanup = sharyFocusTrap(sheet || g);
    }
  };

  const handleGateAdd = (payload) => {
    if (isAuthed()) return false;
    const data = payload || {};
    saveIntent({
      variantId: data.variantId,
      productId: data.productId,
      qty: data.qty,
      qpOpen: data.qpOpen,
      qpProductId: data.qpProductId,
    });
    if (settings.gateEnabled) openGate();
    else redirectToLogin();
    return true;
  };

  window.sharyGateHandleAdd = handleGateAdd;

  const intercept = () => {
    // form-based adds
    document.addEventListener("submit", (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = (form.getAttribute("action") || "").toLowerCase();
      if (action.startsWith("/account/login")) return;
      if (!action.includes("/cart/add")) return;

      if (isAuthed()) return;
      e.preventDefault();

      const fd = new FormData(form);
      const variantId = fd.get("id");
      const qty = fd.get("quantity") || "1";

      saveIntent({ variantId, qty });

      if (settings.gateEnabled) openGate();
      else redirectToLogin();
    }, { capture: true });
  };

  // ✅ FIX: Login WhatsApp helper uses Theme Editor controls (data attrs)
  const injectLoginFallback = () => {
    if (!location.pathname.startsWith("/account/login")) return;
    if (!settings.waEnabled) return;

    const barEnabledAttr = body.getAttribute("data-shary-login-wa-bar-enabled");
    const barEnabled = barEnabledAttr == null ? false : readBool(barEnabledAttr);
    if (!barEnabled) return;

    const wa = buildWaUrl(settings.waMessage);
    if (!wa) return;

    if (document.getElementById("shary-login-wa")) return;

    const txt = readStr(body.getAttribute("data-shary-login-wa-bar-text"));
    if (!txt) return;

    const bar = document.createElement("a");
    bar.id = "shary-login-wa";
    bar.href = wa;
    bar.target = "_blank";
    bar.rel = "noopener noreferrer";
    bar.textContent = txt;

    document.body.appendChild(bar);
  };

  const tryAutoComplete = async () => {
    const intent = readIntent();
    if (!intent) return;
    if (!isAuthed()) return;

    // Hardened: validate intent.url before redirect to block OTP domains
    if (intent.url && typeof intent.url === "string" && location.href !== intent.url) {
      if (isSafeIntentUrl(intent.url)) {
        location.replace(intent.url);
      } else {
        // Unsafe URL detected (possible OTP attack) — user is authed, land on /account
        console.warn("[SHARY V1] Blocked unsafe intent URL:", intent.url);
        clearIntent();
        location.replace("/account");
      }
      return;
    }

    if (typeof intent.scrollY === "number") {
      requestAnimationFrame(() => window.scrollTo(0, intent.scrollY));
    }

    const variantId = intent.variantId ? String(intent.variantId) : null;
    const qty = Math.max(1, parseInt(intent.qty || 1, 10));

    if (!variantId) {
      clearIntent();
      return;
    }

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: variantId, quantity: qty }),
      });

      if (!res.ok) throw new Error("add_failed");
      await res.json();
      
      // Invalidate cart cache after add
      sharyCartStore.invalidate();

      // announce cart updated (badges)
      window.dispatchEvent(new Event("shary:cart-updated"));

      if (intent.qpOpen) {
        window.dispatchEvent(new CustomEvent("shary:intent-restored", {
          detail: { qpOpen: true, qpProductId: intent.qpProductId || null },
        }));
      }

      clearIntent();
    } catch (e) {
      const wa = buildWaUrl("واجهت مشكلة في إكمال الإضافة للسلة. أريد المساعدة.");
      if (wa) window.open(wa, "_blank", "noopener,noreferrer");
      clearIntent();
    }
  };

  intercept();
  injectLoginFallback();
  tryAutoComplete();
})();

/* =========================
   Phase 4 — Atomic UI Components
   Toast + SharyQtyController (Carrefour-style)
   ========================= */
(() => {
  const TOAST_ID = "sharyToastV1";

  const ensureToast = () => {
    let el = document.getElementById(TOAST_ID);
    if (el) return el;
    el = document.createElement("div");
    el.id = TOAST_ID;
    el.className = "shary-toast";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    document.body.appendChild(el);
    return el;
  };

  let toastTimer = null;

  const showToast = (msg) => {
    try {
      const el = ensureToast();
      el.textContent = msg || "";
      el.setAttribute("data-open", "true");
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => el.removeAttribute("data-open"), 1800);
    } catch (e) {}
  };

  window.sharyShowToast = showToast;

  window.addEventListener("shary:toast-fail", (evt) => {
    const msg = (evt && evt.detail && evt.detail.msg) ? evt.detail.msg : "حاول مرة أخرى";
    showToast(msg);
  });
})();

/* =========================
   Phase 4.1 — QuantityManager (Single Cart Controller)
   Smart Quantity Button System — Carrefour-style
   
   PERFORMANCE OPTIMIZATIONS:
   - 80ms debounce (reduced from 120ms)
   - GPU-accelerated CSS transitions (no JS visibility toggling)
   - Single data-state attribute controls UI
   - AbortController for rapid click collapse
   - Minimal DOM queries with early exits
   ========================= */
(() => {
  try {
    // ========================
    // SINGLETON GUARD — prevent duplicate initialization
    // ========================
    if (window.__sharyQuantityManagerInit) return;
    window.__sharyQuantityManagerInit = true;

    const body = document.body;
    if (!body) return;

    // ========================
    // REQUEST QUEUE — per-line collapse + abort
    // ========================
    const qtyQueues = new Map();
    const QUEUE_DELAY_MS = 80; // Reduced from 120ms for faster response

    // ========================
    // UTILITIES
    // ========================
    const isAuthed = () => (parseInt(body.getAttribute("data-shary-auth") || "0", 10) || 0) > 0;

    const parseIntSafe = (v, fallback) => {
      const n = parseInt(String(v || ""), 10);
      return Number.isFinite(n) ? n : fallback;
    };

    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

    const qtyDebugEnabled = () => window.__SHARY_DEBUG_QTY === true;
    const qtyLog = (...args) => {
      if (qtyDebugEnabled()) console.log("[SHARY QTY]", ...args);
    };

    const scheduleQueue = (key) => {
      const entry = qtyQueues.get(key);
      if (!entry) return;
      if (entry.timer) clearTimeout(entry.timer);
      entry.timer = setTimeout(() => processQueue(key), QUEUE_DELAY_MS);
    };

    // ========================
    // SMART ENFORCE CARD STATE — GPU-accelerated state management
    // Uses CSS transforms/opacity via data-state attribute
    // Backward compatible with legacy markup
    // ========================
    const enforceCardState = (cardEl, qty) => {
      if (!cardEl) return;
      const isActive = Number(qty) > 0;
      const stateValue = isActive ? "active" : "idle";
      
      // Update card-level state for CSS safety nets
      cardEl.dataset.qtyState = isActive ? "added" : "empty";
      cardEl.classList.toggle("has-stepper", isActive);
      
      // Find qty wrapper (supports both smart and legacy)
      const qtyWrap = cardEl.querySelector("[data-shary-qty][data-mode='card']");
      if (qtyWrap) {
        // SMART MODE: Just set data-state, CSS handles visibility via transforms
        if (qtyWrap.classList.contains("shary-qty--smart")) {
          qtyWrap.dataset.state = stateValue;
          return;
        }
        
        // LEGACY MODE: Full JS visibility control for backward compatibility
        qtyWrap.dataset.state = isActive ? "added" : "empty";
        
        // Hide legacy plus if present
        const legacyPlus = cardEl.querySelector('[data-shary-legacy-plus]');
        if (legacyPlus) legacyPlus.style.display = isActive ? 'none' : '';
        
        // Legacy add buttons
        const addBtns = cardEl.querySelectorAll(".shary-qty__add:not(.shary-qty__add--smart)");
        addBtns.forEach((btn) => {
          if (!btn.dataset.origDisplay) {
            const cs = window.getComputedStyle(btn);
            btn.dataset.origDisplay = cs.display && cs.display !== "none" ? cs.display : "grid";
          }
          btn.hidden = isActive;
          btn.style.display = isActive ? "none" : btn.dataset.origDisplay;
          btn.style.pointerEvents = isActive ? "none" : "";
          btn.style.visibility = isActive ? "hidden" : "";
          btn.setAttribute("aria-hidden", isActive);
        });
        
        // Legacy pill
        const pill = qtyWrap.querySelector(".shary-qty__pill:not(.shary-qty__pill--smart)");
        if (pill) {
          if (!pill.dataset.origDisplay) {
            const cs = window.getComputedStyle(pill);
            pill.dataset.origDisplay = cs.display && cs.display !== "none" ? cs.display : "flex";
          }
          pill.hidden = !isActive;
          pill.style.display = !isActive ? "none" : pill.dataset.origDisplay;
          pill.style.pointerEvents = !isActive ? "none" : "";
          pill.style.visibility = !isActive ? "hidden" : "";
          pill.setAttribute("aria-hidden", !isActive);
        }
      }
    };

    // Session flag for cart cleared (C)
    const CART_CLEARED_KEY = "shary_cart_cleared";

    // Cart state cache (uses global sharyCartStore)
    const getCartCache = () => sharyCartStore.getCache();

    // ========================
    // CART API METHODS (use centralized store)
    // ========================

    const fetchCart = async () => {
      const cart = await sharyCartStore.fetchCart();
      return cart;
    };

    const addToCart = async (variantId, quantity = 1, signal) => {
      sharyPerf.count('cartAdd');
      sharyPerf.mark('cartAdd:start');
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: String(variantId), quantity }),
        signal,
      });
      if (!res.ok) throw new Error("add_failed");
      const result = await res.json();
      sharyCartStore.invalidate(); // Invalidate cache after mutation
      sharyPerf.mark('cartAdd:end');
      sharyPerf.measure('cartAdd', 'cartAdd:start', 'cartAdd:end');
      return result;
    };

    const changeCartLine = async (line, quantity, signal) => {
      sharyPerf.count('cartChange');
      sharyPerf.mark('cartChange:start');
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ line, quantity }),
        signal,
      });
      if (!res.ok) throw new Error("change_failed");
      const result = await res.json();
      sharyCartStore.invalidate(); // Invalidate cache after mutation
      sharyPerf.mark('cartChange:end');
      sharyPerf.measure('cartChange', 'cartChange:start', 'cartChange:end');
      return result;
    };

    const changeCartByKey = async (key, quantity, signal) => {
      sharyPerf.count('cartChange');
      sharyPerf.mark('cartChangeKey:start');
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity }),
        signal,
      });
      if (!res.ok) throw new Error("change_failed");
      const result = await res.json();
      sharyCartStore.invalidate(); // Invalidate cache after mutation
      sharyPerf.mark('cartChangeKey:end');
      sharyPerf.measure('cartChange', 'cartChangeKey:start', 'cartChangeKey:end');
      return result;
    };

    const getQueueKey = ({ lineKey, line, variantId }) => {
      if (lineKey) return `key-${lineKey}`;
      if (Number.isFinite(line) && line > 0) return `line-${line}`;
      if (variantId) return `variant-${variantId}`;
      return null;
    };

    // Get pending qty from queue (for rapid-tap handling)
    const getPendingQty = (key) => {
      if (!key) return null;
      const entry = qtyQueues.get(key);
      if (entry && entry.desiredQty !== null) return entry.desiredQty;
      return null;
    };

    const getQueueEntry = (key) => {
      if (!qtyQueues.has(key)) {
        qtyQueues.set(key, {
          desiredQty: null,
          ctx: null,
          action: null,
          timer: null,
          controller: null,
          inFlight: false,
        });
      }
      return qtyQueues.get(key);
    };

    const sendCartUpdate = async (ctx, targetQty, signal) => {
      const mode = ctx.mode;
      const lineKey = ctx.lineKey;
      const line = ctx.line;
      const variantId = ctx.variantId;

      if (mode === "cart") {
        if (lineKey) return changeCartByKey(lineKey, targetQty, signal);
        if (line > 0) return changeCartLine(line, targetQty, signal);
      }

      if (variantId) {
        let key = getLineKeyForVariant(variantId);
        if (!key) {
          await fetchCart();
          key = getLineKeyForVariant(variantId);
        }
        if (key) return changeCartByKey(key, targetQty, signal);
        if (targetQty > 0) return addToCart(variantId, targetQty, signal);
      }

      qtyLog("cart_out_of_sync", { lineKey, line, variantId });
      throw new Error("cart_out_of_sync");
    };

    const queueCartUpdate = (ctx, targetQty, action) => {
      const key = getQueueKey(ctx);
      if (!key) return;
      const entry = getQueueEntry(key);
      entry.desiredQty = targetQty;
      entry.ctx = { ...ctx };
      entry.action = action;
      if (entry.controller) {
        entry.controller.abort();
        qtyLog("aborted", { key, qty: targetQty });
      }
      scheduleQueue(key);
    };

    const processQueue = async (key) => {
      const entry = qtyQueues.get(key);
      if (!entry || entry.inFlight) return;
      if (entry.desiredQty == null) return; // Nothing to send
      entry.timer = null;
      entry.inFlight = true;
      const targetQty = entry.desiredQty;
      const ctx = entry.ctx || {};
      const controller = new AbortController();
      entry.controller = controller;

      try {
        await sendCartUpdate(ctx, targetQty, controller.signal);
        if (controller.signal.aborted) return;
        qtyLog("applied", { key, qty: targetQty });
        if (ctx.mode === "cart" && targetQty === 0 && ctx.qtyEl) {
          const row = ctx.qtyEl.closest(".shary-cart__row");
          if (row) {
            const isRtl = document.documentElement.getAttribute("dir") === "rtl";
            row.style.opacity = "0";
            row.style.transform = `translateX(${isRtl ? "-" : ""}100%)`;
            row.style.transition = "opacity 200ms, transform 200ms";
            setTimeout(() => row.remove(), 200);
          }
        }
        // Clear desiredQty if no new pending changes (prevents stale pending reads)
        if (entry.desiredQty === targetQty) {
          entry.desiredQty = null;
        }
        const evtName = entry.action === "add" ? "shary:cart-updated" : "shary:cart-changed";
        window.dispatchEvent(new Event(evtName));
        if (entry.action === "add" && ctx.mode === "qp" && typeof window.sharyQPAfterAdd === "function") {
          window.sharyQPAfterAdd(targetQty);
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        // Clear desiredQty on error to prevent stale reads
        if (entry.desiredQty === targetQty) {
          entry.desiredQty = null;
        }
        window.dispatchEvent(new CustomEvent("shary:toast-fail", { detail: { msg: "حاول مرة أخرى" } }));
        renderAll();
      } finally {
        if (entry.controller === controller) entry.controller = null;
        entry.inFlight = false;
        if (entry.desiredQty != null && entry.desiredQty !== targetQty) scheduleQueue(key);
      }
    };

    // ========================
    // RENDER METHODS
    // ========================

    const moneyFormat = (cents) => {
      const egp = (Number(cents || 0) / 100).toFixed(2);
      return `${egp} EGP`;
    };

    // Badge rendering delegated to Phase 1 via events (no duplication)

    const renderTotals = (cart) => {
      if (!cart || !isAuthed()) return;

      // Update cart total in sticky bar
      const totalEl = document.querySelector("[data-shary-cart-total]");
      if (totalEl) {
        totalEl.textContent = moneyFormat(cart.total_price);
        if (typeof cart.items_subtotal_price === "number") {
          totalEl.setAttribute("data-subtotal-cents", String(cart.items_subtotal_price));
        }
      }
    };

    const renderMinOrder = (cart) => {
      if (!cart || !isAuthed()) return;

      const totalEl = document.querySelector("[data-shary-cart-total]");
      if (!totalEl) return;

      const subCents = parseIntSafe(totalEl.getAttribute("data-subtotal-cents"), 0);
      const minCents = parseIntSafe(totalEl.getAttribute("data-min-cents"), 20000);

      const checkoutBtn = document.querySelector("[data-shary-checkout]");
      if (checkoutBtn) {
        // Enable/disable based on min order (optional - currently just tagging)
        // Future: checkoutBtn.setAttribute('disabled', subCents < minCents);
      }
    };

    const getVariantQtyInCart = (variantId) => {
      const cartCache = getCartCache();
      if (!cartCache || !cartCache.items) return 0;
      const vid = String(variantId);
      const item = cartCache.items.find(i => String(i.variant_id) === vid);
      return item ? item.quantity : 0;
    };

    const getLineKeyForVariant = (variantId) => {
      const cartCache = getCartCache();
      if (!cartCache || !cartCache.items) return null;
      const vid = String(variantId);
      const item = cartCache.items.find(i => String(i.variant_id) === vid);
      return item ? item.key : null;
    };

    // ========================
    // SYNC SINGLE QTY ELEMENT — always derives from cart cache
    // ========================
    const syncQtyElement = (qtyEl, cartCache) => {
      if (!qtyEl || !cartCache) return;
      const mode = qtyEl.getAttribute("data-mode");
      const variantId = qtyEl.getAttribute("data-shary-variant") || qtyEl.getAttribute("data-variant-id");

      if (mode === "card") {
        if (!variantId) return;
        const qty = getVariantQtyInCart(variantId);
        const valEl = qtyEl.querySelector("[data-qty-value]");
        const cardEl = qtyEl.closest(".shary-card");
        if (valEl) valEl.textContent = qty > 0 ? String(qty) : "1";
        qtyEl.setAttribute("data-qty", String(qty));
        enforceCardState(cardEl, qty);
        return;
      }

      if (mode === "cart") {
        const lineRaw = qtyEl.getAttribute("data-shary-line") || qtyEl.getAttribute("data-line");
        const line = parseIntSafe(lineRaw, 0);
        if (line < 1 || !cartCache.items) return;
        const item = cartCache.items[line - 1];
        if (!item) return;
        const qty = item.quantity;
        const valEl = qtyEl.querySelector("[data-qty-value]");
        if (valEl) valEl.textContent = String(qty);
        qtyEl.setAttribute("data-qty", String(qty));
      }
    };

    const renderQtySteppers = () => {
      sharyPerf.count('qtyStepperUpdates');
      sharyPerf.mark('renderQty:start');
      const cartCache = getCartCache();
      if (!cartCache) return;

      // PERFORMANCE: Single DOM query for all qty elements instead of 3 separate queries
      sharyPerf.count('domQueries');
      const allQtyEls = document.querySelectorAll("[data-shary-qty]");
      sharyPerf.log('renderQtySteppers', { count: allQtyEls.length });

      allQtyEls.forEach((qtyEl) => {
        syncQtyElement(qtyEl, cartCache);
      });
      sharyPerf.mark('renderQty:end');
      sharyPerf.measure('renderQtySteppers', 'renderQty:start', 'renderQty:end');
    };

    const renderAll = async () => {
      sharyPerf.count('renderCycles');
      sharyPerf.mark('renderAll:start');
      const cart = await fetchCart();
      if (!cart) return;

      renderQtySteppers();
      renderTotals(cart);
      // Badge rendering delegated to Phase 1 via shary:cart-updated/changed events
      renderMinOrder(cart);
      sharyPerf.mark('renderAll:end');
      sharyPerf.measure('renderAll', 'renderAll:start', 'renderAll:end');
    };

    // ========================
    // EVENT HANDLERS
    // ========================

    // Helper: Optimistic UI update for qty display with smart animation
    const optimisticUpdate = (qtyEl, newQty) => {
      const valEl = qtyEl.querySelector("[data-qty-value]");
      if (valEl) {
        valEl.textContent = String(newQty);
        // Trigger pulse animation for smart buttons
        if (qtyEl.classList.contains("shary-qty--smart")) {
          valEl.setAttribute("data-animating", "true");
          setTimeout(() => valEl.removeAttribute("data-animating"), 150);
        }
      }
      qtyEl.setAttribute("data-qty", String(newQty));
    };

    // Helper: Update PDP mode qty display and hidden input
    const updatePdpQty = (qtyEl, newQty) => {
      const valEl = qtyEl.querySelector("[data-qty-value]");
      const hiddenInput = qtyEl.querySelector("[data-qty-input]");
      if (valEl) valEl.textContent = String(newQty);
      if (hiddenInput) hiddenInput.value = String(newQty);
      qtyEl.setAttribute("data-qty", String(newQty));
    };

    const updateQpQty = (qtyEl, newQty) => {
      const stepper = qtyEl.querySelector("[data-shary-stepper]");
      const input = stepper ? stepper.querySelector("[data-stepper-value]") : null;
      const hidden = stepper ? stepper.querySelector("[data-stepper-hidden]") : null;
      if (input) input.value = String(newQty);
      if (hidden) hidden.value = String(newQty);
      qtyEl.setAttribute("data-qty", String(newQty));
      const addBtn = qtyEl.querySelector("[data-shary-qp-add]");
      if (addBtn) addBtn.setAttribute("data-qty", String(newQty));
    };

    const updateReorderQty = (qtyEl, newQty) => {
      qtyEl.dataset.qty = String(newQty);
      const qtyValue = qtyEl.querySelector("[data-qty-value]");
      if (qtyValue) qtyValue.textContent = String(newQty);
      if (newQty === 0) qtyEl.classList.add("is-removed");
      else qtyEl.classList.remove("is-removed");
    };

    const updateQtyDisplay = (qtyEl, newQty) => {
      const mode = qtyEl.getAttribute("data-mode");
      if (mode === "pdp") return updatePdpQty(qtyEl, newQty);
      if (mode === "qp") return updateQpQty(qtyEl, newQty);
      if (mode === "reorder") return updateReorderQty(qtyEl, newQty);
      optimisticUpdate(qtyEl, newQty);
    };

    const buildQtyContext = (qtyEl, btn) => {
      const mode = qtyEl ? (qtyEl.getAttribute("data-mode") || "") : "";
      const lineRaw = qtyEl ? (qtyEl.getAttribute("data-shary-line") || qtyEl.getAttribute("data-line")) : "";
      const variantId = getVariantId(btn, qtyEl);
      const lineKey = qtyEl ? qtyEl.getAttribute("data-line-key") : null;
      const line = parseIntSafe(lineRaw, 0);
      
      // Check queue for pending qty (prevents race conditions on rapid taps)
      const queueKey = getQueueKey({ lineKey, line, variantId });
      const pendingQty = getPendingQty(queueKey);
      
      // Use pending qty if available, otherwise read from DOM
      const domQty = qtyEl ? (qtyEl.getAttribute("data-qty") || qtyEl.dataset.qty) : null;
      const currentQty = pendingQty !== null ? pendingQty : parseIntSafe(domQty, 1);
      
      return {
        mode,
        variantId,
        lineKey,
        line,
        step: Math.max(1, parseIntSafe(qtyEl ? qtyEl.getAttribute("data-step") : 1, 1)),
        max: Math.max(1, parseIntSafe(qtyEl ? qtyEl.getAttribute("data-max") : 999, 999)),
        currentQty,
        qtyEl,
      };
    };

    // Handle ADD action (card & qp)
    const handleAdd = (qtyEl, ctx, qty) => {
      if (!isAuthed()) return; // Auth gate handles guests
      if (!qtyEl || !ctx.variantId) return;
      const safeQty = Math.max(1, parseIntSafe(qty, 1));
      updateQtyDisplay(qtyEl, safeQty);
      if (ctx.mode === "card") {
        const cardEl = qtyEl.closest(".shary-card");
        enforceCardState(cardEl, safeQty);
      }
      queueCartUpdate(ctx, safeQty, "add");
    };

    // Handle INCREMENT action
    const handleInc = (qtyEl, ctx) => {
      const currentQty = ctx.currentQty;
      if (currentQty >= ctx.max) {
        const msg = qtyEl.getAttribute("data-toast-max") || "وصلت للحد الأقصى";
        if (window.sharyShowToast) window.sharyShowToast(msg);
        return;
      }
      const newQty = clamp(currentQty + ctx.step, 1, ctx.max);
      updateQtyDisplay(qtyEl, newQty);
      if (ctx.mode === "card") {
        const cardEl = qtyEl.closest(".shary-card");
        enforceCardState(cardEl, newQty);
      }
      if (ctx.mode === "pdp" || ctx.mode === "qp" || ctx.mode === "reorder") return;
      queueCartUpdate(ctx, newQty, "inc");
    };

    // Handle DECREMENT action
    const handleDec = (qtyEl, ctx) => {
      const min = (ctx.mode === "pdp" || ctx.mode === "qp") ? 1 : 0;
      const newQty = Math.max(min, ctx.currentQty - ctx.step);
      updateQtyDisplay(qtyEl, newQty);
      if (ctx.mode === "card") {
        const cardEl = qtyEl.closest(".shary-card");
        enforceCardState(cardEl, newQty);
      }
      if (ctx.mode === "pdp" || ctx.mode === "qp" || ctx.mode === "reorder") return;
      queueCartUpdate(ctx, newQty, "dec");
    };

    // ========================
    // CLICK DELEGATION (B) — single handler for add/inc/dec
    // ========================

    const resolveQtyEl = (btn) => {
      if (!btn) return null;
      return btn.closest("[data-shary-qty]")
        || btn.closest(".shary-card")?.querySelector("[data-shary-qty]")
        || null;
    };

    const getVariantId = (btn, qtyEl) => {
      if (!btn && !qtyEl) return null;
      const btnVariant = btn ? btn.getAttribute("data-shary-variant") : null;
      const qtyVariant = qtyEl ? qtyEl.getAttribute("data-shary-variant") : null;
      let vid = btnVariant || qtyVariant || null;
      if (!vid && btn && btn.dataset) vid = btn.dataset.variantId || null;
      if (!vid && qtyEl) vid = qtyEl.getAttribute("data-variant-id");
      if (!vid) {
        const selector = "[data-shary-variant], [data-variant-id]";
        const btnHolder = btn && btn.closest ? btn.closest(selector) : null;
        const qtyHolder = !btnHolder && qtyEl && qtyEl.closest ? qtyEl.closest(selector) : null;
        const holder = btnHolder || qtyHolder;
        if (holder) vid = holder.getAttribute("data-shary-variant") || holder.getAttribute("data-variant-id");
      }
      if (!vid) {
        const root = btn.closest("form") || btn.closest(".shary-card") || document;
        const input = root ? root.querySelector("input[name='id']") : null;
        if (input && input.value) vid = input.value;
      }
      return vid ? String(vid) : null;
    };

    const getQty = (btn, qtyEl) => {
      const raw = btn?.dataset?.qty || qtyEl?.getAttribute("data-qty") || "1";
      const parsed = parseIntSafe(raw, 1);
      return Math.max(1, parsed);
    };

    const resolveAction = (btn) => {
      if (!btn) return null;
      if (btn.hasAttribute("data-shary-add")) return "add";
      if (btn.hasAttribute("data-shary-qty-plus")) return "inc";
      if (btn.hasAttribute("data-shary-qty-minus")) return "dec";
      const action = btn.getAttribute("data-action");
      if (action === "inc" || action === "dec" || action === "add") return action;
      return null;
    };

    const calculateTargetQty = (action, ctx, btn, qtyEl) => {
      if (action === "add") return Math.max(1, parseIntSafe(getQty(btn, qtyEl), 1));
      if (!ctx) return 1;
      if (action === "inc") return clamp(ctx.currentQty + ctx.step, 1, ctx.max);
      if (action === "dec") {
        const min = (ctx.mode === "pdp" || ctx.mode === "qp") ? 1 : 0;
        return Math.max(min, ctx.currentQty - ctx.step);
      }
      return 1;
    };

    const handleQtyAction = (btn, e, actionOverride) => {
      if (isLoginPage()) return;
      if (!btn) return;

      if (btn.disabled || btn.getAttribute("aria-disabled") === "true") {
        qtyLog("ignored disabled", btn);
        return;
      }

      const qtyEl = resolveQtyEl(btn);
      if (!qtyEl && !btn.hasAttribute("data-shary-add")) return;

      const action = actionOverride || resolveAction(btn);
      if (!action) return;

      const inQtyContext = !!qtyEl || !!btn.closest(".shary-card") || !!btn.closest("[data-shary-qp]");
      if (action === "add" && !inQtyContext) return;

      if (qtyEl && getCartCache()) syncQtyElement(qtyEl, getCartCache());

      const ctx = qtyEl ? buildQtyContext(qtyEl, btn) : null;
      const targetQty = calculateTargetQty(action, ctx, btn, qtyEl);

      qtyLog("click", {
        action,
        targetQty,
        source: btn.getAttribute("aria-label") || btn.className || btn.tagName
      });

      if (action === "add" && !isAuthed()) {
        const payload = {
          variantId: ctx ? ctx.variantId : getVariantId(btn, qtyEl),
          productId: btn.getAttribute("data-product-id") || qtyEl?.getAttribute("data-product-id"),
          qty: targetQty,
          qpOpen: btn.getAttribute("data-qp-open") === "true",
          qpProductId: btn.getAttribute("data-qp-product-id"),
        };
        if (typeof window.sharyGateHandleAdd === "function" && window.sharyGateHandleAdd(payload)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      // Prevent default and stop propagation immediately for instant feel
      e.preventDefault();
      e.stopPropagation();

      try {
        switch (action) {
          case "add":
            if (!qtyEl || !ctx || !ctx.variantId) {
              qtyLog("missing add context", { qtyEl: !!qtyEl, variantId: !!(ctx && ctx.variantId) });
              return;
            }
            handleAdd(qtyEl, ctx, getQty(btn, qtyEl));
            break;
          case "inc":
            if (!qtyEl || !ctx) return;
            handleInc(qtyEl, ctx);
            break;
          case "dec":
            if (!qtyEl || !ctx) return;
            handleDec(qtyEl, ctx);
            break;
        }
      } catch (err) {
        qtyLog("action error", err);
      }
    };

    // Single delegated click handler (covers dynamic DOM)
    sharyPerf.trackListener(document, "click-qty");
    document.addEventListener("click", (e) => {
      try {
        const btn = e.target.closest("[data-shary-add], [data-shary-qty-plus], [data-shary-qty-minus]");
        if (!btn) return;
        handleQtyAction(btn, e);
      } catch (err) {
        console.error("[Shary][qty] crash prevented", err);
      }
    });

    // ========================
    // KEYBOARD SUPPORT — Arrow keys for qty stepper
    // ArrowUp/'+' => increment, ArrowDown/'-' => decrement
    // Delegated listener - no per-element binding needed
    // ========================
    sharyPerf.trackListener(document, 'keydown');
    document.addEventListener("keydown", (e) => {
      // Early guard: Only allow if focus is inside qty UI or quick preview
      const active = document.activeElement;
      if (!active) return;
      const body = document.body;
      // a) active inside [data-shary-qty]
      // b) active is button/input within qty
      // c) body.shary-qp-open and active inside quick preview sheet
      const qtyRoot = active.closest && active.closest('[data-shary-qty]');
      const isQtyBtnOrInput = qtyRoot && (active.matches('button, input'));
      const isQP = body.classList.contains('shary-qp-open') && active.closest && active.closest('.shary-qp__sheet');
      if (!(qtyRoot || isQtyBtnOrInput || isQP)) return;

      const qtyEl = active.closest && active.closest("[data-shary-qty]");
      if (!qtyEl) return;

      const key = e.key;
      let action = null;
      if (key === "ArrowUp" || key === "+") {
        action = "inc";
      } else if (key === "ArrowDown" || key === "-") {
        action = "dec";
      }
      if (!action) return;

      e.preventDefault();

      // Find the button for this action to delegate to handleQtyAction
      const btn = qtyEl.querySelector(`[data-action="${action}"], ${action === "inc" ? "[data-shary-qty-plus]" : "[data-shary-qty-minus]"}`);
      if (btn && !btn.disabled) {
        // Synthesize a click-like event for the action handler
        handleQtyAction(btn, e, action);
      }
    });

    // ========================
    // SECTION RELOAD HANDLER — re-init newly loaded qty elements
    // ========================
    document.addEventListener("shopify:section:load", (e) => {
      renderAll();
    });

    // ========================
    // INITIALIZATION
    // ========================

    // Check if cart was cleared (C) — sync UI on return from WhatsApp
    const checkCartCleared = async () => {
      try {
        const cleared = sharyStorage.session.get(CART_CLEARED_KEY);
        if (cleared === "1") {
          sharyStorage.session.remove(CART_CLEARED_KEY);
          // Force fetch fresh cart state and sync UI
          await renderAll();
          window.dispatchEvent(new Event("shary:cart-updated"));
        }
      } catch (_) {}
    };

    // Initial render
    renderAll();
    checkCartCleared();

    // Listen for external cart updates
    sharyPerf.trackListener(window, 'shary:cart-updated-renderAll');
    window.addEventListener("shary:cart-updated", () => renderAll());
    sharyPerf.trackListener(window, 'shary:cart-changed-renderAll');
    window.addEventListener("shary:cart-changed", () => renderAll());

    let qtySyncTimer = null;
    const QTY_SYNC_DELAY_MS = 60;
    const scheduleQtySync = () => {
      if (qtySyncTimer) return;
      qtySyncTimer = setTimeout(() => {
        qtySyncTimer = null;
        const cache = getCartCache();
        if (cache) renderQtySteppers();
        else renderAll();
      }, QTY_SYNC_DELAY_MS);
    };

    const qtyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes || []) {
          if (!(node instanceof Element)) continue;
          if (node.matches("[data-shary-qty]") || node.querySelector("[data-shary-qty]")) {
            scheduleQtySync();
            return;
          }
        }
      }
    });

    qtyObserver.observe(document.body, { childList: true, subtree: true });

  } catch (e) {
    console.warn("[SHARY] QuantityManager error:", e);
  }
})();

/* =========================
   Phase 4.2 — Pack Selector (علبة/كرتونة)
   Delegated listener for switching pack type on product cards
   ========================= */
(() => {
  // Idempotent guard
  if (window.__sharyPackSelectorInit) return;
  window.__sharyPackSelectorInit = true;

  // ========================
  // PACK SWITCH QTY HELPERS
  // ========================

  /**
   * Reset qty UI to empty state (qty 0).
   * Temporary visual reset to prevent showing wrong qty while resync happens.
   */
  const resetCardQtyUI = (card) => {
    if (!card) return;
    const qtyEl = card.querySelector("[data-shary-qty][data-mode='card']");
    if (!qtyEl) return;

    // Reset qty to 0
    qtyEl.setAttribute("data-qty", "0");
    const valEl = qtyEl.querySelector("[data-qty-value]");
    if (valEl) valEl.textContent = "0";

    // Show add button, hide pill stepper (match QuantityManager enforceCardState logic)
    const addBtns = card.querySelectorAll(".shary-qty__add");
    addBtns.forEach((btn) => {
      if (!btn.dataset.origDisplay) {
        const cs = window.getComputedStyle(btn);
        btn.dataset.origDisplay = cs.display && cs.display !== "none" ? cs.display : "grid";
      }
      btn.hidden = false;
      btn.style.display = btn.dataset.origDisplay;
      btn.style.pointerEvents = "";
      btn.style.visibility = "";
      btn.setAttribute("aria-hidden", "false");
    });

    card.dataset.qtyState = "empty";
    card.classList.remove("has-stepper");
    qtyEl.dataset.state = "empty";

    const pill = qtyEl.querySelector(".shary-qty__pill");
    if (pill) {
      pill.hidden = true;
      pill.style.display = "none";
      pill.style.pointerEvents = "none";
      pill.style.visibility = "hidden";
      pill.setAttribute("aria-hidden", "true");
    }
  };

  /**
   * Resync card qty from cart for the new variantId.
   * Uses centralized cart store for deduplication.
   */
  const resyncCardQtyFromCart = async (card, variantId) => {
    if (!card || !variantId) return;

    // Use centralized cart store
    const cart = await sharyCartStore.fetchCart();

    if (!cart || !cart.items) return;

    // Find qty for this variantId
    const vid = String(variantId);
    const item = cart.items.find(i => String(i.variant_id) === vid);
    const qty = item ? item.quantity : 0;

    const qtyEl = card.querySelector("[data-shary-qty][data-mode='card']");
    if (!qtyEl) return;

    // Update qty attribute and display
    qtyEl.setAttribute("data-qty", String(qty));
    const valEl = qtyEl.querySelector("[data-qty-value]");
    if (valEl) valEl.textContent = qty > 0 ? String(qty) : "1";

    // Toggle UI: if qty>0 show pill stepper & hide add button; else vice versa
    const isAdded = qty > 0;

    const addBtns = card.querySelectorAll(".shary-qty__add");
    addBtns.forEach((btn) => {
      if (!btn.dataset.origDisplay) {
        const cs = window.getComputedStyle(btn);
        btn.dataset.origDisplay = cs.display && cs.display !== "none" ? cs.display : "grid";
      }
      btn.hidden = isAdded;
      btn.style.display = isAdded ? "none" : btn.dataset.origDisplay;
      btn.style.pointerEvents = isAdded ? "none" : "";
      btn.style.visibility = isAdded ? "hidden" : "";
      btn.setAttribute("aria-hidden", isAdded ? "true" : "false");
    });

    card.dataset.qtyState = isAdded ? "added" : "empty";
    card.classList.toggle("has-stepper", isAdded);
    qtyEl.dataset.state = isAdded ? "added" : "empty";

    const pill = qtyEl.querySelector(".shary-qty__pill");
    if (pill) {
      if (!pill.dataset.origDisplay) {
        const cs = window.getComputedStyle(pill);
        pill.dataset.origDisplay = cs.display && cs.display !== "none" ? cs.display : "flex";
      }
      pill.hidden = !isAdded;
      pill.style.display = !isAdded ? "none" : pill.dataset.origDisplay;
      pill.style.pointerEvents = !isAdded ? "none" : "";
      pill.style.visibility = !isAdded ? "hidden" : "";
      pill.setAttribute("aria-hidden", !isAdded ? "true" : "false");
    }
  };

  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-shary-pack-btn]");
    if (!btn) return;

    const card = btn.closest("[data-shary-card]");
    if (!card) return;

    const pack = btn.dataset.pack;
    if (!pack) return;

    let packMap;
    try {
      packMap = JSON.parse(card.dataset.packMap || "{}");
    } catch (err) {
      sharyLog("Pack selector: invalid packMap JSON", err);
      return;
    }

    const packData = packMap[pack];
    if (!packData || !packData.id) return;

    const newVariantId = String(packData.id);
    const newCents = packData.cents;

    // Update card data attributes
    card.dataset.variantId = newVariantId;
    card.dataset.pack = pack;
    if (typeof newCents !== "undefined") {
      card.dataset.priceCents = newCents;
    }

    // Update ALL data-variant-id nodes inside card (qty stepper, add button, etc.)
    card.querySelectorAll("[data-variant-id]").forEach((el) => {
      el.setAttribute("data-variant-id", newVariantId);
    });
    card.querySelectorAll("[data-shary-variant]").forEach((el) => {
      el.setAttribute("data-shary-variant", newVariantId);
    });

    // Reset qty UI immediately to prevent showing wrong qty
    resetCardQtyUI(card);

    // Resync qty from cart for the new variant (async, non-blocking)
    resyncCardQtyFromCart(card, newVariantId);

    // Swap price HTML from template
    const priceWrap = card.querySelector(".shary-card__price");
    const tpl = card.querySelector(`template[data-pack-price="${pack}"]`);
    if (priceWrap && tpl) {
      priceWrap.innerHTML = tpl.innerHTML;
    }

    // Update pack label in meta (only if it's not the pack selector itself)
    const packLabel = card.querySelector(".shary-card__pack");
    if (packLabel && !packLabel.classList.contains("shary-pack")) {
      packLabel.textContent = pack;
      packLabel.classList.remove("shary-card__pack--empty");
      packLabel.removeAttribute("aria-hidden");
    }

    // Update aria-pressed states
    const packWrap = card.querySelector("[data-shary-pack]");
    if (packWrap) {
      packWrap.querySelectorAll("[data-shary-pack-btn]").forEach((b) => {
        b.setAttribute("aria-pressed", b === btn ? "true" : "false");
      });
    }
  });
})();

/* =========================
   Phase 6 — Listing Page (Rail + Grid Stability)
   ========================= */
(() => {
  try {
    const root = document.querySelector("[data-shary-listing]");
    if (!root) return;

    const rail = root.querySelector("[data-shary-rail]");
    const grid = root.querySelector("[data-shary-grid]");
    if (!rail || !grid) return;

    const handle = root.getAttribute("data-collection-handle") || "collection";
    const KEY_SUB = `shary_v1_subcat_${handle}`;
    const KEY_SCROLL = `shary_v1_scroll_${handle}`;

    const cells = Array.from(grid.querySelectorAll(".shary-listing__cell"));
    const buttons = Array.from(rail.querySelectorAll("[data-subcat]"));
    const anchorEl = root.querySelector(".shary-listing__gridWrap") || root;
    const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

    // Performance: Pre-parse subcategory keys at initialization (O(1) lookup vs O(n) per filter)
    const cellSubcatsCache = new Map();
    cells.forEach(cell => {
      const keys = (cell.getAttribute("data-subcats") || "")
        .split("|").map(s => s.trim()).filter(Boolean);
      cellSubcatsCache.set(cell, new Set(keys));
    });

    const readCssNumber = (value) => {
      const n = parseFloat(String(value || "").replace("px", "").trim());
      return Number.isFinite(n) ? n : 0;
    };

    const docStyle = getComputedStyle(document.documentElement);
    const topbarHeight = readCssNumber(docStyle.getPropertyValue("--shary-topbar-h")) || 56;
    const safeTop = readCssNumber(docStyle.getPropertyValue("--shary-safe-area-top"));
    const snapOffset = topbarHeight + safeTop + 12;

    const clampScroll = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return 0;
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return Math.max(0, Math.min(Math.round(num), max));
    };

    const listingTopTarget = () =>
      clampScroll((window.scrollY || 0) + anchorEl.getBoundingClientRect().top - snapOffset);

    const scrollListingIntoView = () => {
      if (isDesignMode) return;
      const target = listingTopTarget();
      if (Math.abs((window.scrollY || 0) - target) > 2) window.scrollTo(0, target);
    };

    const afterLayout = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

    const saveScrollPosition = (value) => {
      if (isDesignMode) return;
      const target = typeof value === "number" ? value : listingTopTarget();
      sharyStorage.session.set(KEY_SCROLL, String(clampScroll(target)));
    };

    const loadScrollPosition = () => {
      if (isDesignMode) return 0;
      const raw = sharyStorage.session.get(KEY_SCROLL);
      if (raw == null) return 0;
      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) ? clampScroll(parsed) : 0;
    };

    let pendingScrollSave = false;
    const scheduleScrollSave = () => {
      if (isDesignMode || pendingScrollSave) return;
      pendingScrollSave = true;
      afterLayout(() => {
        pendingScrollSave = false;
        saveScrollPosition(listingTopTarget());
      });
    };

    const buttonKeys = new Set(
      buttons
        .map((b) => b.getAttribute("data-subcat") || "")
        .map((k) => k.trim())
        .filter(Boolean)
    );
    buttonKeys.add("__all");

    const sanitizeKey = (key) => {
      const target = (key || "").trim() || "__all";
      return buttonKeys.has(target) ? target : "__all";
    };

    const getSelected = () => sharyStorage.session.get(KEY_SUB) || "__all";

    const resolveInitialKey = () => {
      const saved = getSelected();
      const safe = sanitizeKey(saved);
      if (safe === "__all" && saved !== "__all") sharyStorage.session.remove(KEY_SUB);
      return safe;
    };

    const setPressed = (key) => {
      const safe = sanitizeKey(key);
      buttons.forEach((b) =>
        b.setAttribute("aria-pressed", b.getAttribute("data-subcat") === safe ? "true" : "false")
      );
    };

    const setLoading = (on) => root.setAttribute("data-loading", on ? "true" : "false");

    const matchCell = (cell, key) => {
      if (key === "__all") return true;
      const keys = cellSubcatsCache.get(cell);
      return keys && keys.has(key);
    };

    const applyFilter = (rawKey) => {
      const key = sanitizeKey(rawKey);
      setLoading(true);

      requestAnimationFrame(() => {
        for (const cell of cells) cell.hidden = !matchCell(cell, key);
        setTimeout(() => setLoading(false), 120);
      });

      return key;
    };

    const computeCounts = () => {
      const counts = new Map();
      counts.set("__all", cells.length);

      for (const cell of cells) {
        const keys = cellSubcatsCache.get(cell);
        if (keys) {
          for (const k of keys) counts.set(k, (counts.get(k) || 0) + 1);
        }
      }

      rail.querySelectorAll("[data-count-for]").forEach((el) => {
        const k = el.getAttribute("data-count-for");
        if (!k) return;
        const n = counts.get(k);
        el.textContent = Number.isFinite(n) ? String(n) : "";
      });
    };

    const initial = resolveInitialKey();
    setPressed(initial);
    computeCounts();
    let activeKey = applyFilter(initial);

    if (!isDesignMode) {
      const savedScroll = loadScrollPosition();
      if (savedScroll > 0) {
        afterLayout(() => window.scrollTo(0, savedScroll));
      }

      window.addEventListener("scroll", scheduleScrollSave, { passive: true });
    }

    rail.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-subcat]");
      if (!btn) return;
      const requested = btn.getAttribute("data-subcat") || "__all";
      const key = sanitizeKey(requested);

      if (key === activeKey) return;

      if (key === "__all") sharyStorage.session.remove(KEY_SUB);
      else sharyStorage.session.set(KEY_SUB, key);

      setPressed(key);
      activeKey = applyFilter(key);

      if (!isDesignMode) {
        afterLayout(() => {
          scrollListingIntoView();
          scheduleScrollSave();
        });
      }
    });

    window.addEventListener("shary:qty-change", (evt) => { void evt; });
  } catch (e) {}
})();

/* =========================
   Phase 6.2 — Scroll to Subcategory Rail
   ========================= */
(() => {
  document.addEventListener("DOMContentLoaded", () => {
    const body = document.body;
    if (!body) return;

    const readBool = window.Shary.readBool;
    const readInt = window.Shary.readInt;
    const readStr = window.Shary.readStr;

    const scrollEnabled = readBool(body.getAttribute("data-shary-subcat-scroll-enabled"), true);
    const durationMs = Math.max(0, readInt(body.getAttribute("data-shary-subcat-scroll-ms"), 450));
    const easingKey = readStr(body.getAttribute("data-shary-subcat-scroll-easing")) || "ease";
    const reduceMotion = readBool(body.getAttribute("data-shary-reduce-motion")) ||
      (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

    const readCssNumber = (value) => {
      const n = parseFloat(String(value || "").replace("px", "").trim());
      return Number.isFinite(n) ? n : 0;
    };

    const getHeaderOffset = () => {
      const docStyle = getComputedStyle(document.documentElement);
      const topbarEff = readCssNumber(docStyle.getPropertyValue("--shary-topbar-effective-h"));
      const topbarBase = readCssNumber(docStyle.getPropertyValue("--shary-topbar-h")) || 56;
      const topbar = topbarEff || topbarBase;
      const safeTop = readCssNumber(docStyle.getPropertyValue("--shary-safe-area-top"));
      const ann = body.classList.contains("has-announcement") ? readCssNumber(docStyle.getPropertyValue("--shary-ann-h")) : 0;
      const buffer = 6;
      return Math.max(0, topbar + safeTop + ann + buffer);
    };

    const clampY = (y) => {
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return Math.max(0, Math.min(Math.round(y), max));
    };

    const easingFns = {
      ease: (t) => 1 - Math.pow(1 - t, 3),
      linear: (t) => t,
      "ease-in-out": (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
    };

    const animateScrollTo = (targetY) => {
      const startY = window.scrollY || window.pageYOffset || 0;
      const destY = clampY(targetY);
      const effectiveDuration = reduceMotion ? 0 : durationMs;

      if (effectiveDuration <= 0) {
        window.scrollTo(0, destY);
        return;
      }

      const startTs = performance.now();
      const easeFn = easingFns[easingKey] || easingFns.ease;

      const step = (now) => {
        const progress = Math.min(1, (now - startTs) / effectiveDuration);
        const eased = easeFn(progress);
        const nextY = startY + (destY - startY) * eased;
        window.scrollTo(0, nextY);
        if (progress < 1) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    };

    const findRail = () =>
      document.querySelector("[data-shary-subcategory-rail]") ||
      document.querySelector(".shary-subrail");

    const scrollToRail = () => {
      const rail = findRail();
      if (!rail) return false;
      const y = (window.scrollY || 0) + rail.getBoundingClientRect().top - getHeaderOffset();
      animateScrollTo(y);
      return true;
    };

    document.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-shary-scroll-to-subcats]");
      if (!trigger) return;
      if (!scrollEnabled) return;
      const didScroll = scrollToRail();
      if (!didScroll) return;
      e.preventDefault();
    }, true);
  });
})();

/* =========================
   Phase 6.5 — Home Categories → Listing swap (Collection by selected category)
   ========================= */
(() => {
  try {
    const listing = document.querySelector("[data-shary-listing][data-section-id]");
    if (!listing) return;

    const sectionId = listing.getAttribute("data-section-id");
    if (!sectionId) return;

    const setLoading = (on) => listing.setAttribute("data-loading", on ? "true" : "false");

    const replaceListingFromHtml = (html) => {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;

      const next = tmp.querySelector("[data-shary-listing][data-section-id]");
      if (!next) return false;

      listing.replaceWith(next);
      return true;
    };

    const fetchSection = async (handle) => {
      const url = `/collections/${encodeURIComponent(handle)}?sections=${encodeURIComponent(sectionId)}`;
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) throw new Error("section_fetch_failed");
      const json = await res.json();
      const html = json && json[sectionId];
      if (!html) throw new Error("section_missing");
      return html;
    };

    document.addEventListener("click", async (e) => {
      try {
        if (isLoginPage()) return;
        const tile = e.target.closest("[data-shary-categories] .shary-catTile[data-collection-handle]");
        if (!tile) return;

        const handle = (tile.getAttribute("data-collection-handle") || "").trim();
        if (!handle) return;

        // prevent full navigation (app-like)
        e.preventDefault();

        // store last selected category
        sharyStorage.session.set("shary_active_collection_v1", handle);

        setLoading(true);

        try {
          const html = await fetchSection(handle);
          const ok = replaceListingFromHtml(html);
          if (!ok) throw new Error("replace_failed");

          // smooth scroll to listing
          requestAnimationFrame(() => {
            const newListing = document.querySelector("[data-shary-listing]");
            if (!newListing) return;
            const top = newListing.getBoundingClientRect().top + (window.scrollY || 0);
            window.scrollTo(0, Math.max(0, top - 80));
          });
        } catch (err) {
          window.dispatchEvent(new CustomEvent("shary:toast-fail", { detail: { msg: "حاول مرة أخرى" } }));
        } finally {
          const current = document.querySelector("[data-shary-listing]");
          if (current) current.removeAttribute("data-loading");
        }
      } catch (outerErr) {
        console.error("[Shary][category] crash prevented", outerErr);
      }
    }, { passive: false });

    // auto-restore last chosen category on reload (optional)
    try {
      const saved = sharyStorage.session.get("shary_active_collection_v1");
      if (saved && !listing.getAttribute("data-collection-handle")) {
        setLoading(true);
        fetchSection(saved)
          .then((html) => replaceListingFromHtml(html))
          .catch(() => {})
          .finally(() => {
            const current = document.querySelector("[data-shary-listing]");
            if (current) current.removeAttribute("data-loading");
          });
      }
    } catch (_) {}
  } catch (e) {}
})();

/* =========================
   Phase 6.6 — Reorder Page (Orders replacement)
   ========================= */
(() => {
  try {
    const root = document.querySelector("[data-shary-reorder]");
    if (!root) return;

    const body = document.body;
    const waEnabled = (body.getAttribute("data-shary-wa-enabled") || "true").toLowerCase() === "true";
    const waPhone = (body.getAttribute("data-shary-wa-phone") || "").replace(/[^\d]/g, "");
    const waMessage = (body.getAttribute("data-shary-wa-message") || "").trim();

    const parseJson = (txt) => {
      try { return JSON.parse(txt); } catch (_) { return null; }
    };

    const orders = {};
    root.querySelectorAll("script[data-shary-order-json]").forEach((script) => {
      const id = script.getAttribute("data-order-id");
      const payload = parseJson(script.textContent || "");
      if (!id || !payload || !Array.isArray(payload.items)) return;

      const items = payload.items
        .filter((item) => item && item.variantId)
        .map((item) => ({
          variantId: String(item.variantId),
          quantity: Math.max(1, parseInt(item.quantity || 1, 10)),
          title: item.title || "",
          price: Number(item.price || 0),
          currentPrice: Number(item.currentPrice || item.price || 0),
          available: !!item.available,
        }));

      orders[id] = {
        id,
        name: payload.name || `طلب #${id}`,
        date: payload.date || "",
        status: payload.status || "",
        items,
      };
    });

    const hero = root.querySelector("[data-reorder-hero]");
    const defaultOrderId = hero ? hero.getAttribute("data-order-id") : Object.keys(orders)[0];
    if (!defaultOrderId || !orders[defaultOrderId]) return;

    const listEl = root.querySelector("[data-shary-reorder-list]");
    const editSection = root.querySelector("[data-shary-reorder-edit]");
    const selectedEl = root.querySelector("[data-shary-reorder-selected]");
    const confirmEl = root.querySelector("[data-shary-reorder-confirm]");
    const changesEl = root.querySelector("[data-shary-reorder-changes]");
    const prevListEl = root.querySelector("[data-shary-reorder-prev]");
    const prevIcon = root.querySelector("[data-shary-reorder-prev-icon]");

    let currentOrderId = defaultOrderId;
    let pendingAction = null;

    const setEditVisible = (on) => {
      if (!editSection) return;
      editSection.hidden = !on;
    };

    const renderList = (orderId) => {
      if (!listEl) return;
      const order = orders[orderId];
      currentOrderId = orderId;

      if (selectedEl && order) selectedEl.textContent = order.name || "طلب سابق";

      listEl.innerHTML = "";
      if (!order || !order.items.length) {
        const empty = document.createElement("div");
        empty.className = "shary-reorder__muted";
        empty.textContent = "لا توجد أصناف في هذا الطلب.";
        listEl.appendChild(empty);
        return;
      }

      order.items.forEach((item, index) => {
        const row = document.createElement("div");
        const priceChanged = Number(item.price || 0) !== Number(item.currentPrice || item.price || 0);
        row.className = "shary-reorder__line";
        row.dataset.variantId = item.variantId;
        row.dataset.sharyQty = "";
        row.dataset.mode = "reorder";
        row.dataset.sharyVariant = String(item.variantId || "");
        row.dataset.sharyLine = String(index + 1);
        row.dataset.step = "1";
        row.dataset.max = "999";
        row.dataset.title = item.title || "";
        row.dataset.price = String(item.price || 0);
        row.dataset.currentPrice = String(item.currentPrice || item.price || 0);
        row.dataset.available = item.available ? "true" : "false";
        row.dataset.qty = String(item.quantity || 1);

        row.innerHTML = `
          <div class="shary-reorder__lineInfo">
            <div class="shary-reorder__lineTitle">${item.title || "منتج"}</div>
            <div class="shary-reorder__lineMeta">
              <span>${item.available ? "متاح" : "غير متوفر"}</span>
              ${priceChanged ? `<span class="shary-reorder__pill">تم تحديث السعر</span>` : ""}
            </div>
          </div>
          <div class="shary-reorder__lineActions">
            <button type="button" class="shary-reorder__qtybtn" data-action="inc" data-shary-qty-plus aria-label="زيادة الكمية">+</button>
            <span class="shary-reorder__statValue" data-qty-value>${item.quantity}</span>
            <button type="button" class="shary-reorder__qtybtn" data-action="dec" data-shary-qty-minus aria-label="تقليل الكمية">-</button>
            <button type="button" class="shary-reorder__remove" data-action="remove">حذف</button>
          </div>
        `;

        listEl.appendChild(row);
      });
    };

    const updateQty = (row, delta) => {
      const current = parseInt(row.dataset.qty || "1", 10);
      const next = Math.max(0, current + delta);
      row.dataset.qty = String(next);
      const valEl = row.querySelector("[data-qty-value]");
      if (valEl) valEl.textContent = String(next);
      if (next === 0) row.classList.add("is-removed");
      else row.classList.remove("is-removed");
    };

    const collectFromDom = () => {
      const items = [];
      const changes = [];
      if (!listEl) return { items, changes };

      listEl.querySelectorAll(".shary-reorder__line").forEach((row) => {
        const variantId = row.dataset.variantId;
        const qty = parseInt(row.dataset.qty || "0", 10);
        const title = row.dataset.title || "منتج";
        const available = row.dataset.available === "true";
        const price = Number(row.dataset.price || 0);
        const currentPrice = Number(row.dataset.currentPrice || price);

        if (!variantId || qty <= 0) {
          if (qty <= 0) changes.push(`تمت إزالة ${title}`);
          return;
        }

        if (!available) {
          changes.push(`غير متوفر: ${title}`);
          return;
        }

        if (price !== currentPrice) changes.push(`تم تحديث السعر: ${title}`);

        items.push({ variantId, quantity: qty, title, price, currentPrice });
      });

      return { items, changes };
    };

    const collectFromOrder = (orderId) => {
      const order = orders[orderId];
      const items = [];
      const changes = [];
      if (!order) return { items, changes, label: "" };

      order.items.forEach((item) => {
        if (!item.variantId) return;
        if (!item.available) {
          changes.push(`غير متوفر: ${item.title || "منتج"}`);
          return;
        }
        if (Number(item.price || 0) !== Number(item.currentPrice || item.price || 0)) {
          changes.push(`تم تحديث السعر: ${item.title || "منتج"}`);
        }
        items.push({
          variantId: item.variantId,
          quantity: item.quantity,
          title: item.title,
          price: item.price,
          currentPrice: item.currentPrice,
        });
      });

      return { items, changes, label: order.name || "طلب سابق" };
    };

    const showConfirm = (changes, action) => {
      if (!changes || !changes.length || !confirmEl || !changesEl) {
        action();
        return;
      }

      changesEl.innerHTML = "";
      changes.forEach((msg) => {
        const li = document.createElement("li");
        li.textContent = msg;
        changesEl.appendChild(li);
      });

      confirmEl.hidden = false;
      pendingAction = action;
    };

    const hideConfirm = () => {
      if (confirmEl) confirmEl.hidden = true;
      pendingAction = null;
    };

    const addItemsToCart = async (items) => {
      if (!items || !items.length) throw new Error("empty");

      const payload = { items: items.map((item) => ({ id: item.variantId, quantity: item.quantity })) };
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("add_failed");
      await res.json();
      
      // Invalidate cart cache after add
      sharyCartStore.invalidate();
      
      window.dispatchEvent(new Event("shary:cart-updated"));
    };

    const openWhatsApp = (items) => {
      if (!waEnabled || !waPhone) return false;

      const normalizedItems = (items || []).map((item) => ({
        title: item.title,
        quantity: item.quantity,
        price: item.currentPrice || item.price
      }));

      const message = sharyBuildCartCanonicalMessage({ items: normalizedItems, totals: null, meta: {} });
      if (!message) return false;

      const url = sharyBuildWhatsAppUrl(waPhone, message);
      if (!url) return false;

      window.open(url, "_blank", "noopener,noreferrer");
      return true;
    };

    const completeReorder = async (items, changes, label) => {
      if (!items || !items.length) {
        window.dispatchEvent(new CustomEvent("shary:toast-fail", { detail: { msg: "لا توجد أصناف متاحة" } }));
        return;
      }

      try {
        await addItemsToCart(items);
        hideConfirm();
        const opened = openWhatsApp(items);
        if (!opened) location.href = "/cart";
      } catch (_) {
        window.dispatchEvent(new CustomEvent("shary:toast-fail", { detail: { msg: "تعذر إعادة الطلب" } }));
      }
    };

    const handleSubmit = () => {
      const { items, changes } = collectFromDom();
      const label = orders[currentOrderId] ? orders[currentOrderId].name : "طلب سابق";
      const proceed = () => completeReorder(items, changes, label);
      if (changes.length) showConfirm(changes, proceed);
      else proceed();
    };

    const startQuickReorder = (orderId) => {
      const { items, changes, label } = collectFromOrder(orderId || currentOrderId);
      const proceed = () => completeReorder(items, changes, label);
      if (changes.length) showConfirm(changes, proceed);
      else proceed();
    };

    root.addEventListener("click", (e) => {
      const removeBtn = e.target.closest(".shary-reorder__lineActions [data-action='remove']");
      if (removeBtn) {
        const row = removeBtn.closest(".shary-reorder__line");
        if (!row) return;
        e.preventDefault();
        updateQty(row, -999);
        return;
      }

      const quick = e.target.closest("[data-shary-reorder-quick]");
      if (quick) {
        e.preventDefault();
        const orderId = quick.getAttribute("data-shary-reorder-quick");
        if (orderId && orders[orderId]) renderList(orderId);
        startQuickReorder(orderId);
        return;
      }

      const toggleEdit = e.target.closest("[data-shary-reorder-edit-toggle]");
      if (toggleEdit) {
        e.preventDefault();
        const willOpen = editSection ? editSection.hidden : true;
        renderList(currentOrderId);
        setEditVisible(willOpen);
        return;
      }

      const submitBtn = e.target.closest("[data-shary-reorder-submit]");
      if (submitBtn) {
        e.preventDefault();
        handleSubmit();
        return;
      }

      const togglePrev = e.target.closest("[data-shary-reorder-prev-toggle]");
      if (togglePrev && prevListEl) {
        const willOpen = prevListEl.hidden;
        prevListEl.hidden = !willOpen;
        if (prevIcon) prevIcon.textContent = willOpen ? "-" : "+";
        return;
      }

      const confirmGo = e.target.closest("[data-shary-reorder-confirm-continue]");
      if (confirmGo && pendingAction) {
        confirmGo.disabled = true;
        pendingAction();
        confirmGo.disabled = false;
        return;
      }

      const confirmBack = e.target.closest("[data-shary-reorder-confirm-back]");
      if (confirmBack) {
        hideConfirm();
      }
    });

    renderList(defaultOrderId);
    setEditVisible(false);
    hideConfirm();
  } catch (e) {
    console.warn("[SHARY] Reorder error:", e);
  }
})();

/* =========================
   Phase 7 — Quick Preview Bottom Sheet (PDP replacement)
   ========================= */
(() => {
  try {
    const body = document.body;
    if (!body) return;

    const isAuthed = () => (parseInt(body.getAttribute("data-shary-auth") || "0", 10) || 0) > 0;
    const SS_KEY = "shary_qp_last_v1";

    const saveLast = (payload) => { sharyStorage.session.set(SS_KEY, JSON.stringify(payload)); };
    const readLast = () => {
      try {
        const raw = sharyStorage.session.get(SS_KEY);
        if (!raw) return null;
        const d = JSON.parse(raw);
        return d && typeof d === "object" ? d : null;
      } catch (e) { return null; }
    };

    const lockBody = (on) => on ? body.classList.add("shary-qp-open") : body.classList.remove("shary-qp-open");

    // Focus trap cleanup and opener for Quick Preview
    let qpTrapCleanup = null;
    let qpOpener = null;

    const closeQP = () => {
      const qp = document.getElementById("sharyQP");
      if (!qp) return;
      qp.removeAttribute("data-open");
      lockBody(false);
      // Cleanup focus trap
      if (qpTrapCleanup) { qpTrapCleanup(); qpTrapCleanup = null; }
      // Restore focus to opener
      if (qpOpener && typeof qpOpener.focus === "function") {
        try { qpOpener.focus(); } catch (_) {}
      }
      qpOpener = null;
    };

    const ensureQP = () => {
      let qp = document.getElementById("sharyQP");
      if (qp) return qp;

      qp = document.createElement("div");
      qp.id = "sharyQP";
      qp.className = "shary-qp";
      qp.setAttribute("data-shary-qp", "1");
      qp.setAttribute("role", "dialog");
      qp.setAttribute("aria-modal", "true");
      qp.setAttribute("aria-label", "معاينة المنتج");

      qp.innerHTML = `
        <div class="shary-qp__backdrop" data-qp-close="1"></div>
        <div class="shary-qp__sheet" role="document">
          <button class="shary-qp__close" type="button" aria-label="إغلاق" data-qp-close="1">✕</button>

          <div class="shary-qp__row">
            <img class="shary-qp__img" alt="" data-qp-img />
            <div>
              <p class="shary-qp__title" data-qp-title></p>
              <p class="shary-qp__meta" data-qp-pack></p>
              <div class="shary-qp__price" data-qp-price></div>
            </div>
          </div>

          <div class="shary-qp__actions" data-shary-qty data-mode="qp" data-shary-variant="" data-shary-line="" data-step="1" data-max="999" data-qty="1">
            <button class="shary-qp__add" type="button" data-shary-add data-action="add" data-shary-qp-add>+ إضافة</button>

            <div class="shary-qp__stepperWrap" data-shary-qp-stepperwrap>
              <div class="shary-stepper" data-shary-stepper data-qty-target='[data-shary-qp-add]'>
                <button class="shary-stepper__btn" type="button" aria-label="زيادة" data-stepper-plus data-shary-qty-plus>+</button>
                <input class="shary-stepper__value" type="number" inputmode="numeric" min="1" value="1" data-stepper-value />
                <button class="shary-stepper__btn" type="button" aria-label="تقليل" data-stepper-minus data-shary-qty-minus>−</button>
                <input type="hidden" value="1" data-stepper-hidden />
              </div>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(qp);

      qp.addEventListener("click", (e) => {
        if (e.target.closest("[data-qp-close]")) closeQP();
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeQP();
      });

      return qp;
    };

    const setQPState = (qp, state) => {
      if (!qp) return;
      if (state === "added") qp.setAttribute("data-state", "added");
      else qp.removeAttribute("data-state");
    };

    const openQPFromPayload = (payload) => {
      // Store opener for focus restore on close
      qpOpener = document.activeElement;
      const qp = ensureQP();

      const img = qp.querySelector("[data-qp-img]");
      const title = qp.querySelector("[data-qp-title]");
      const pack = qp.querySelector("[data-qp-pack]");
      const price = qp.querySelector("[data-qp-price]");
      const qtyWrap = qp.querySelector("[data-shary-qty][data-mode='qp']");
      const addBtn = qp.querySelector("[data-shary-qp-add]");
      const stepper = qp.querySelector("[data-shary-stepper]");

      if (title) title.textContent = payload.title || "";
      if (pack) pack.textContent = payload.pack || "";

      if (img) {
        const src = payload.img || "";
        if (src) { img.src = src; img.style.visibility = "visible"; }
        else { img.removeAttribute("src"); img.style.visibility = "hidden"; }
        img.alt = payload.title || "";
      }

      if (price) price.innerHTML = payload.priceHtml || "";

      if (addBtn) {
        addBtn.setAttribute("data-variant-id", String(payload.variantId || ""));
        addBtn.setAttribute("data-shary-variant", String(payload.variantId || ""));
        addBtn.setAttribute("data-product-id", String(payload.productId || ""));
        addBtn.setAttribute("data-qty", String(payload.qty || 1));
        addBtn.setAttribute("data-qp-open", "true");
        addBtn.setAttribute("data-qp-product-id", String(payload.productId || ""));
      }

      if (qtyWrap) {
        qtyWrap.setAttribute("data-shary-variant", String(payload.variantId || ""));
        qtyWrap.setAttribute("data-shary-line", "");
        qtyWrap.setAttribute("data-qty", String(payload.qty || 1));
      }

      if (stepper) {
        const val = Math.max(1, parseInt(String(payload.qty || 1), 10) || 1);
        const input = stepper.querySelector("[data-stepper-value]");
        const hidden = stepper.querySelector("[data-stepper-hidden]");
        if (input) input.value = String(val);
        if (hidden) hidden.value = String(val);
      }

      setQPState(qp, payload.state || "default");
      qp.setAttribute("data-open", "true");
      lockBody(true);
      // Enable focus trap on the sheet inside QP
      const sheet = qp.querySelector(".shary-qp__sheet");
      qpTrapCleanup = sharyFocusTrap(sheet || qp);
    };

    const openQPFromCard = (card) => {
      const productId = card.getAttribute("data-product-id") || "";
      const variantId = card.getAttribute("data-variant-id") || "";
      const title = card.getAttribute("data-title") || "";
      const pack = card.getAttribute("data-pack") || "";
      const img = card.getAttribute("data-img") || "";
      const state = card.getAttribute("data-state") === "added" ? "added" : "default";
      const addBtn = card.querySelector("[data-shary-card-add]");
      const qty = addBtn ? (parseInt(addBtn.getAttribute("data-qty") || "1", 10) || 1) : 1;

      const priceEl = card.querySelector(".shary-card__price");
      const priceHtml = priceEl ? priceEl.innerHTML : "";

      const payload = { productId, variantId, title, pack, img, priceHtml, qty, state };
      saveLast(payload);
      openQPFromPayload(payload);
    };

    document.addEventListener("click", (e) => {
      try {
        if (isLoginPage()) return;
        const trig = e.target.closest("[data-shary-qp-trigger]");
        if (!trig) return;

        const card = trig.closest("[data-shary-card]");
        if (!card) return;

        const a = trig.closest("a");
        if (a) e.preventDefault();

        openQPFromCard(card);
      } catch (err) {
        console.error("[Shary][qp] crash prevented", err);
      }
    }, { capture: true });

    document.addEventListener("keydown", (e) => {
      if (!(e.key === "Enter" || e.key === " " || e.code === "Space")) return;
      const el = document.activeElement;
      if (!el) return;
      const trig = el.closest ? el.closest("[data-shary-qp-trigger]") : null;
      if (!trig) return;
      const card = trig.closest("[data-shary-card]");
      if (!card) return;
      e.preventDefault();
      openQPFromCard(card);
    }, { capture: true });

    window.sharyQPAfterAdd = (qty) => {
      const qp = document.getElementById("sharyQP");
      if (qp) setQPState(qp, "added");
      const last = readLast();
      if (last) saveLast({ ...last, state: "added", qty });
    };

    window.addEventListener("shary:intent-restored", () => {
      const last = readLast();
      if (!last) return;
      openQPFromPayload({ ...last, state: "added" });
    });

  } catch (e) {}
})();

/* =========================
   Phase 8 — Cart (Invoice mode + Sticky Checkout Bar)
   NOTE: Cart qty logic now handled by Phase 4.1 SharyQtyController
   This section handles cart page empty state detection
   ========================= */
(() => {
  try {
    const cartRoot = document.querySelector("[data-shary-cart]");
    if (!cartRoot) return;

    // Watch for cart becoming empty after item removal
    const checkEmpty = () => {
      const items = cartRoot.querySelectorAll(".shary-cart__row");
      if (items.length === 0) {
        // Reload page to show empty cart state
        setTimeout(() => location.reload(), 300);
      }
    };

    // Check for empty cart after animations complete
    window.addEventListener("shary:cart-changed", () => {
      setTimeout(checkEmpty, 300);
    });

  } catch (e) {}
})();

/* =========================
   Phase 8.1 — Cart Rules (Min Total + Min Items)
   Single engine: read rules, update checkout state, block WhatsApp if required
   ========================= */
const sharyReadCartRules = () => {
  const root = document.querySelector("[data-shary-cart]");
  if (!root) {
    return {
      root: null,
      enabled: false,
      minTotalEgp: 0,
      minItems: 0,
      message: "",
      blockWhatsapp: false,
    };
  }

  return {
    root,
    enabled: window.Shary.readBool(root.getAttribute("data-cart-rules-enabled")),
    minTotalEgp: Math.max(0, window.Shary.readInt(root.getAttribute("data-cart-min-total-egp"), 0)),
    minItems: Math.max(0, window.Shary.readInt(root.getAttribute("data-cart-min-items"), 0)),
    message: window.Shary.readStr(root.getAttribute("data-cart-rules-message")),
    blockWhatsapp: window.Shary.readBool(root.getAttribute("data-cart-block-whatsapp"), true),
  };
};

const sharySetCheckoutState = (disabled, reason) => {
  const checkoutBtn = document.querySelector("[data-shary-checkout]");
  if (checkoutBtn) {
    if (disabled) {
      checkoutBtn.setAttribute("aria-disabled", "true");
    } else {
      checkoutBtn.removeAttribute("aria-disabled");
    }
  }

  const msgEl = document.querySelector("[data-shary-cart-rules-msg]");
  if (!msgEl) return;
  if (disabled && reason) {
    msgEl.textContent = reason;
    msgEl.hidden = false;
  } else {
    msgEl.textContent = "";
    msgEl.hidden = true;
  }
};

const sharyEvaluateCartRules = (cart) => {
  const rules = sharyReadCartRules();
  if (!rules.root) {
    return { disabled: false, reason: "", blockWhatsapp: false };
  }

  const items = Array.isArray(cart?.items) ? cart.items : [];
  const itemCount = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const subtotalCents = Number(cart?.items_subtotal_price || 0);
  const minTotalCents = Math.max(0, rules.minTotalEgp) * 100;
  const defaultRuleMsg = rules.message || "الحد الأدنى للطلب غير مستوفى.";

  if (itemCount === 0) {
    return { disabled: true, reason: "السلة فارغة", blockWhatsapp: rules.blockWhatsapp };
  }

  if (!rules.enabled) {
    return { disabled: false, reason: "", blockWhatsapp: rules.blockWhatsapp };
  }

  if (minTotalCents > 0 && subtotalCents < minTotalCents) {
    return { disabled: true, reason: defaultRuleMsg, blockWhatsapp: rules.blockWhatsapp };
  }

  if (rules.minItems > 0 && itemCount < rules.minItems) {
    return { disabled: true, reason: defaultRuleMsg, blockWhatsapp: rules.blockWhatsapp };
  }

  return { disabled: false, reason: "", blockWhatsapp: rules.blockWhatsapp };
};

(() => {
  try {
    const cartRoot = document.querySelector("[data-shary-cart]");
    if (!cartRoot) return;
    if (cartRoot.dataset.cartRulesBound === "1") return;
    cartRoot.dataset.cartRulesBound = "1";

    const evaluateAndRender = async () => {
      const cart = await sharyCartStore.fetchCart(true);
      const result = sharyEvaluateCartRules(cart);
      sharySetCheckoutState(result.disabled, result.reason);
    };

    evaluateAndRender();

    window.addEventListener("shary:cart-changed", () => {
      evaluateAndRender();
    });
  } catch (e) {}
})();

/* =========================
   Phase 8.2 — Subrail Debug Helper
   Logs visibility requirements when SHARY_DEBUG is enabled
   ========================= */
(() => {
  try {
    if (window.SHARY_DEBUG !== true) return;
    const rail = document.querySelector(".shary-subrail");
    if (rail) {
      console.log("[SHARY DEBUG] Subrail: present");
    } else {
      console.log("[SHARY DEBUG] Subrail: missing. Requires shary_subrail_enabled + shary_subrail_menu + page toggle.");
    }
  } catch (e) {}
})();

/* =========================
   Phase 8.8 — Shared Utilities for WhatsApp Checkout/Reorder
   Common functions used by checkout and reorder flows
   ========================= */
const sharyWaMoneyFormat = (cents) => {
  const egp = (Number(cents || 0) / 100).toFixed(2);
  return `${egp} ج.م`;
};

/* =========================
   Phase 8.8.1 — Ledger Fetch Utility
   Unified fetch wrapper for App Proxy endpoints with robust error handling
   ========================= */
const sharyLedgerFetch = async (path, options = {}) => {
  const url = path.startsWith("/") ? path : `/${path}`;
  const defaults = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
    },
    credentials: "same-origin",
  };
  const config = { ...defaults, ...options };
  if (options.body && typeof options.body === "object") {
    config.body = JSON.stringify(options.body);
  }
  
  const res = await fetch(url, config);
  const text = await res.text();
  
  let data;
  try {
    data = JSON.parse(text);
  } catch (_) {
    data = { ok: false, error: text || "Invalid JSON response" };
  }
  
  if (!res.ok) {
    const error = new Error(data.error || data.message || `HTTP ${res.status}`);
    error.status = res.status;
    error.data = data;
    throw error;
  }
  
  return data;
};

/* =========================
   Phase 8.8.1.1 — Ledger List Deduplication Store
   Shared promise cache to avoid duplicate list fetches
   ========================= */
const sharyLedgerListStore = (() => {
  const cache = new Map();
  const pending = new Map();

  const fetchList = async (url) => {
    if (cache.has(url)) return cache.get(url);
    if (pending.has(url)) return pending.get(url);

    const request = sharyLedgerFetch(url)
      .then((data) => {
        cache.set(url, data);
        return data;
      })
      .finally(() => {
        pending.delete(url);
      });

    pending.set(url, request);
    return request;
  };

  return { fetchList };
})();

/* =========================
   Phase 8.8.2 — Debug Mode Utilities
   Fixed overlay panel for debug=1 mode
   ========================= */
const sharyDebugEnabled = () => /[?&]debug=1/.test(location.search);

const sharyDebugPanels = new Map();

const sharyDebugPanel = (key, title = "DEBUG") => {
  if (!sharyDebugEnabled()) return { log: () => {}, update: () => {}, clear: () => {} };
  
  let panel = sharyDebugPanels.get(key);
  let logs = [];
  
  if (!panel) {
    panel = document.createElement("div");
    panel.className = "shary-debug-panel";
    panel.setAttribute("data-shary-debug", key);
    panel.style.cssText = `
      position: fixed;
      top: 64px;
      left: 12px;
      right: 12px;
      z-index: 999999;
      background: rgba(0, 0, 0, 0.92);
      color: #00ff88;
      padding: 12px 16px;
      font-size: 11px;
      font-family: 'SF Mono', Consolas, monospace;
      max-height: 40vh;
      overflow-y: auto;
      border-radius: 8px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.5);
      border: 1px solid rgba(0,255,136,0.3);
      direction: ltr;
      text-align: left;
    `;
    document.body.appendChild(panel);
    sharyDebugPanels.set(key, panel);
  }
  
  const render = () => {
    panel.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;padding-bottom:8px;border-bottom:1px solid rgba(0,255,136,0.2);">
        <span style="background:#f47c20;color:#000;font-weight:800;font-size:9px;border-radius:3px;padding:2px 6px;">DEBUG</span>
        <strong style="color:#fff;">${title}</strong>
        <span style="margin-inline-start:auto;opacity:0.5;font-size:9px;">${new Date().toLocaleTimeString()}</span>
      </div>
      ${logs.map(entry => `
        <div style="margin:3px 0;display:flex;align-items:flex-start;gap:8px;">
          <span style="width:18px;text-align:center;flex-shrink:0;">${entry.icon}</span>
          <span style="color:${entry.color || '#00ff88'};">${entry.label}</span>
          ${entry.detail ? `<span style="opacity:0.6;font-size:10px;word-break:break-all;">${entry.detail}</span>` : ''}
        </div>
      `).join("")}
    `;
  };
  
  const icons = { ok: "✅", fail: "❌", pending: "⏳", info: "ℹ️", warn: "⚠️" };
  const colors = { ok: "#00ff88", fail: "#ff4444", pending: "#ffaa00", info: "#88ccff", warn: "#ffaa00" };
  
  return {
    log: (label, status, detail = null) => {
      logs.push({ 
        label, 
        icon: icons[status] || "•", 
        color: colors[status] || "#00ff88",
        detail: detail ? String(detail) : null 
      });
      console.log(`[SHARY ${key.toUpperCase()}] ${status.toUpperCase()}: ${label}`, detail || "");
      render();
    },
    update: render,
    clear: () => { logs = []; render(); }
  };
};

/* =========================
   Phase 8.8.3 — Reliable Cart Clear
   Combined sendBeacon + keepalive for maximum reliability
   ========================= */
const sharyClearCartReliable = async () => {
  const clearPayload = JSON.stringify({});
  let beaconSent = false;
  let fetchOk = false;
  
  // Invalidate cart cache immediately
  sharyCartStore.invalidate();
  
  // Primary: sendBeacon (survives page navigation)
  if (navigator.sendBeacon) {
    beaconSent = navigator.sendBeacon("/cart/clear.js", new Blob([clearPayload], { type: "application/json" }));
  }
  
  // Secondary: fetch with keepalive
  try {
    const res = await fetch("/cart/clear.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: clearPayload,
      credentials: "same-origin",
      keepalive: true,
    });
    fetchOk = res.ok;
  } catch (_) {}
  
  return { beaconSent, fetchOk };
};

/* =========================
   Phase 8.8.4 — Base64 UTF-8 Safe Encode/Decode
   ========================= */
const sharyBase64Encode = (str) => {
  try {
    return btoa(unescape(encodeURIComponent(str)));
  } catch (_) {
    return btoa(str);
  }
};

const sharyBase64Decode = (str) => {
  try {
    return decodeURIComponent(escape(atob(str)));
  } catch (_) {
    return atob(str);
  }
};

const sharyBuildCartCanonicalMessage = ({ items, totals, meta } = {}) => {
  if (!items || items.length === 0) return null;

  let msg = "";
  const orderReference = meta && typeof meta.orderReference === "string"
    ? meta.orderReference.trim()
    : meta && meta.orderReference != null
      ? String(meta.orderReference).trim()
      : "";

  if (orderReference && orderReference !== "null" && orderReference !== "undefined") {
    msg += `رقم الطلب: ${orderReference}\n`;
    msg += "━━━━━━━━━━━━━━━\n";
  }

  msg += "طلب جديد من شاري 🧾\n";
  msg += "━━━━━━━━━━━━━━━\n";

  let totalPrice = 0;
  let itemCount = 0;

  items.forEach((item, idx) => {
    const rawTitle = item.product_title || item.title;
    const title = rawTitle && rawTitle !== "null" && rawTitle !== "undefined" ? rawTitle : "منتج";
    const variant = item.variant_title && item.variant_title !== "Default Title"
      ? ` (${item.variant_title})`
      : "";
    const qty = item.qty || item.quantity || 1;
    const lineTotal = item.final_line_price || item.line_price || ((item.price || 0) * qty);
    totalPrice += lineTotal;
    itemCount += qty;

    msg += `${idx + 1}) ${title}${variant}\n`;
    msg += `   الكمية: ${qty}\n`;
    if (lineTotal > 0) {
      msg += `   الإجمالي: ${sharyWaMoneyFormat(lineTotal)}\n`;
    }
    if (idx < items.length - 1) msg += "\n";
  });

  const finalTotal = totals && typeof totals.totalPrice === "number" ? totals.totalPrice : totalPrice;
  const finalCount = totals && typeof totals.itemCount === "number" ? totals.itemCount : itemCount;

  msg += "━━━━━━━━━━━━━━━\n";
  if (finalTotal > 0) {
    msg += `الإجمالي: ${sharyWaMoneyFormat(finalTotal)}\n`;
  }
  msg += `عدد الأصناف: ${finalCount}\n`;
  msg += "━━━━━━━━━━━━━━━\n";
  msg += "العنوان + طريقة الدفع:";

  return msg;
};

/**
 * Clear cart before page navigation (best-effort).
 * Uses dual strategy for maximum reliability:
 * - sendBeacon: survives page navigation (best-effort, not guaranteed)
 * - fetch with keepalive: iOS Safari fallback, additional reliability
 * Note: Neither method guarantees delivery if page unloads immediately,
 * but combined they provide robust coverage across browsers.
 */
const sharyClearCart = async () => {
  const clearPayload = JSON.stringify({});
  
  // Invalidate cart cache immediately
  sharyCartStore.invalidate();
  
  // Primary: sendBeacon (survives page navigation, best-effort)
  if (navigator.sendBeacon) {
    navigator.sendBeacon("/cart/clear.js", new Blob([clearPayload], { type: "application/json" }));
  }
  
  // Secondary: fetch with keepalive (iOS Safari compatibility, additional reliability)
  fetch("/cart/clear.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: clearPayload,
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => {});
};

/* =========================
   Phase 8.9 — Reorder Vault (90-day persistence for WhatsApp orders)
   Stores cart items client-side. Called from WhatsApp checkout before redirect.
   ========================= */
const SHARY_REORDER_KEY = "shary_reorder_v1";
const SHARY_REORDER_TTL_DAYS = 90;

const sharyReorderLoad = () => {
  try {
    const raw = sharyStorage.local.get(SHARY_REORDER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const sharyReorderPrune = (list, now) => {
  const ttlMs = SHARY_REORDER_TTL_DAYS * 24 * 60 * 60 * 1000;
  return list.filter((item) => {
    const lastSeenAt = Number(item.lastSeenAt) || 0;
    return (now - lastSeenAt) <= ttlMs;
  });
};

const sharyReorderSave = (list) => {
  try {
    sharyStorage.local.set(SHARY_REORDER_KEY, JSON.stringify(list));
  } catch (_) {}
};

const sharyReorderMergeFromCart = (cart) => {
  if (!cart || !cart.items || cart.items.length === 0) return;
  const now = Date.now();
  let list = sharyReorderLoad();
  list = sharyReorderPrune(list, now);

  const map = new Map();
  list.forEach((item) => map.set(String(item.variant_id), item));

  cart.items.forEach((item) => {
    const vid = String(item.variant_id);
    const existing = map.get(vid);
    const record = {
      variant_id: item.variant_id,
      product_id: item.product_id || null,
      handle: item.handle || null,
      title: item.product_title || item.title || "",
      image: item.image || item.featured_image?.url || null,
      price: item.final_price != null ? item.final_price : (item.price || null),
      lastSeenAt: now
    };
    if (existing) {
      // Update lastSeenAt and patch missing fields
      if (!existing.handle && record.handle) existing.handle = record.handle;
      if (!existing.image && record.image) existing.image = record.image;
      if (existing.price == null && record.price != null) existing.price = record.price;
      if (!existing.title && record.title) existing.title = record.title;
      existing.lastSeenAt = now;
    } else {
      map.set(vid, record);
    }
  });

  const merged = Array.from(map.values());
  sharyReorderSave(merged);
};

/* =========================
   Phase 9 — WhatsApp Checkout (Send cart to WhatsApp)
   Reads phone from Theme Editor via data-wa-phone attribute
   Integrates with Order Ledger (Metaobjects via App Proxy)
   HARD GATE: WhatsApp CANNOT open unless snapshot is CONFIRMED
   Debug mode: ?debug=1 shows on-page operation status overlay
   ========================= */
(() => {
  try {
    const checkoutBtn = document.querySelector("[data-shary-checkout]");
    if (!checkoutBtn) return;
    if (checkoutBtn.dataset.bound === "1") return;
    checkoutBtn.dataset.bound = "1";

    const body = document.body;
    // IDENTITY FIX: Use separate attributes for auth flag vs customer ID
    const isAuthed = () => (parseInt(body?.getAttribute("data-shary-auth") || "0", 10) || 0) > 0;
    const getCustomerId = () => body?.getAttribute("data-shary-customer-id") || "0";

    if (!isAuthed()) return;

    // Use unified debug panel utility
    let debug = null;

    // Generate idempotency key: hash(customerId + variant_id:qty pairs + minute bucket)
    const generateIdempotencyKey = (customerId, cartItems) => {
      const minuteBucket = Math.floor(Date.now() / (60 * 1000));
      const itemsStr = (cartItems || [])
        .map((item) => `${item.variant_id}:${item.quantity}`)
        .sort()
        .join("|");
      const raw = `${customerId}|${itemsStr}|${minuteBucket}`;
      // Simple hash for browser (not crypto-strength, but deterministic)
      let hash = 0;
      for (let i = 0; i < raw.length; i++) {
        const chr = raw.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0;
      }
      return Math.abs(hash).toString(16).padStart(8, "0");
    };

    // Create order snapshot via App Proxy — MUST SUCCEED (hard gate)
    // NO try/catch swallowing - errors propagate to abort WhatsApp
    const createOrderSnapshot = async (customerId, cart, idempotencyKey) => {
      debug.log("Sending snapshot request...", "pending", `idem: ${idempotencyKey}`);
      
      try {
        const data = await sharyLedgerFetch("/apps/shary-ledger/snapshot", {
          method: "POST",
          body: {
            customer_id: customerId, // Server will IGNORE this, uses signed query param
            cart: cart,
            idempotency_key: idempotencyKey,
            source: "whatsapp",
          },
        });
        
        if (!data.ok) {
          debug.log("Snapshot NOT OK", "fail", data.error || data.message || "Unknown");
          throw new Error(`Snapshot failed: ${data.error || data.message || "Unknown error"}`);
        }
        
        if (!data.reference) {
          debug.log("Snapshot missing reference", "fail");
          throw new Error("Snapshot succeeded but no reference returned");
        }
        
        debug.log("Snapshot SUCCESS", "ok", `ref: ${data.reference}`);
        
        if (data.retention) {
          debug.log("Retention", "info", `kept: ${data.retention.kept}, deleted: ${data.retention.deleted}`);
        }
        
        return {
          ok: true,
          reference: data.reference,
          id: data.id,
          customer_id: data.customer_id,
          retention: data.retention
        };
      } catch (err) {
        debug.log("Snapshot HTTP failed", "fail", `${err.status || "?"}: ${err.message}`);
        throw err;
      }
    };

    // Use shared utility for WhatsApp message
    const buildWhatsAppMessage = (cart, orderReference) => {
      if (!cart || !cart.items || cart.items.length === 0) return null;
      return sharyBuildCartCanonicalMessage({
        items: cart.items,
        totals: null,
        meta: { orderReference }
      });
    };

    checkoutBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Initialize debug panel on click
      debug = sharyDebugPanel("checkout", "CHECKOUT FLOW");
      debug.clear();
      
      debug.log("Checkout click detected", "ok");

      const waPhone = (checkoutBtn.getAttribute("data-wa-phone") || "").replace(/[^\d]/g, "");
      if (!waPhone) {
        debug.log("WhatsApp phone missing", "fail");
        alert("رقم الواتساب غير مُعد. يرجى التواصل مع الدعم.");
        return;
      }
      debug.log("WhatsApp phone", "ok", waPhone);

      // HARD VALIDATION: Customer ID must exist
      const customerId = getCustomerId();
      if (!customerId || customerId === "0" || customerId === "") {
        debug.log("NO CUSTOMER ID", "fail", "data-shary-customer-id missing or 0");
        alert("خطأ: لم يتم التعرف على العميل. يرجى تسجيل الدخول مرة أخرى.");
        return;
      }
      debug.log("Customer ID", "ok", customerId);

      // Read clear cart setting from data attribute
      const shouldClearCart = (checkoutBtn.getAttribute("data-clear-cart") || "true").toLowerCase() === "true";

      checkoutBtn.setAttribute("aria-busy", "true");
      checkoutBtn.style.pointerEvents = "none";
      const originalText = checkoutBtn.textContent;
      checkoutBtn.textContent = "جاري الإرسال...";

      try {
        // Step 1: Fetch cart (force fresh data for checkout)
        debug.log("Fetching cart...", "pending");
        sharyCartStore.invalidate(); // Ensure fresh data
        const cart = await sharyCartStore.fetchCart(true);
        if (!cart) {
          debug.log("Cart fetch failed", "fail");
          throw new Error("cart_fetch_failed");
        }

        if (!cart.items || cart.items.length === 0) {
          debug.log("Cart is empty", "fail");
          alert("السلة فارغة");
          return;
        }
        debug.log("Cart fetched", "ok", `${cart.items.length} items`);

        const rulesResult = sharyEvaluateCartRules(cart);
        sharySetCheckoutState(rulesResult.disabled, rulesResult.reason);
        if (rulesResult.disabled && rulesResult.blockWhatsapp) {
          if (rulesResult.reason) alert(rulesResult.reason);
          throw new Error("cart_rules_blocked");
        }

        // Step 2: Create snapshot — MANDATORY, NO FALLBACK
        // HARD GATE: WhatsApp cannot open unless snapshot succeeds
        const idempotencyKey = generateIdempotencyKey(customerId, cart.items);
        const snapshotResult = await createOrderSnapshot(customerId, cart, idempotencyKey);
        
        // If we reach here, snapshot MUST have succeeded (createOrderSnapshot throws on failure)
        const orderReference = snapshotResult.reference;
        debug.log("Server customer_id", "info", snapshotResult.customer_id || "not returned");

        // Step 3: Build message WITH reference
        const message = buildWhatsAppMessage(cart, orderReference);
        if (!message) {
          debug.log("Message build failed", "fail");
          throw new Error("Failed to build WhatsApp message");
        }
        debug.log("Message built", "ok", `${message.length} chars`);

        // Step 4: Save to localStorage for reorder fallback (backup only)
        if (typeof sharyReorderMergeFromCart === "function") {
          sharyReorderMergeFromCart(cart);
          debug.log("LocalStorage backup", "ok");
        }

        const waUrl = sharyBuildWhatsAppUrl(waPhone, message);
        if (!waUrl) {
          debug.log("WhatsApp URL invalid", "fail");
          throw new Error("wa_url_invalid");
        }
        
        // Step 5: Clear cart ONLY after snapshot confirmed
        if (shouldClearCart) {
          debug.log("Clearing cart...", "pending");
          
          // Update UI immediately
          const cartList = document.querySelector(".shary-cart__list");
          const cartEmpty = document.querySelector(".shary-cart__empty");
          
          if (cartList) {
            cartList.innerHTML = "";
            if (cartEmpty) {
              cartEmpty.hidden = false;
            } else {
              cartList.innerHTML = '<div class="shary-cart__empty"><p class="shary-cart__emptyTitle">سلتك فارغة</p></div>';
            }
          }
          
          // Update ALL cart badges
          document.querySelectorAll("[data-shary-cart-badge]").forEach((badge) => {
            badge.textContent = "0";
            badge.hidden = true;
          });
          
          // Update cartbar total
          const totalEl = document.querySelector("[data-shary-cart-total]");
          if (totalEl) {
            totalEl.textContent = "0.00 ج.م";
            totalEl.setAttribute("data-subtotal-cents", "0");
          }

          // Set session flag for re-verification on return
          sharyStorage.session.set("shary_cart_cleared_v1", "1");

          // Use reliable cart clear (sendBeacon + keepalive)
          const clearResult = await sharyClearCartReliable();
          debug.log("Cart cleared", "ok", `beacon:${clearResult.beaconSent}, fetch:${clearResult.fetchOk}`);
        }

        // Step 6: Open WhatsApp — ONLY after everything succeeded
        debug.log("Opening WhatsApp", "ok", orderReference);
        window.dispatchEvent(new Event("shary:cart-updated"));
        window.location.href = waUrl;

      } catch (err) {
        // HARD FAILURE: Do NOT open WhatsApp, do NOT clear cart
        debug.log("CHECKOUT ABORTED", "fail", err.message);
        console.error("[SHARY] Checkout failed:", err);

        if (err.message === "cart_rules_blocked") {
          return;
        }
        
        alert("فشل في حفظ الطلب. يرجى المحاولة مرة أخرى.\n\n" + err.message);
        
      } finally {
        checkoutBtn.removeAttribute("aria-busy");
        checkoutBtn.style.pointerEvents = "";
        checkoutBtn.textContent = originalText;
      }
    }, { passive: false });

  } catch (e) {
    console.error("[SHARY] Phase 9 init error:", e);
  }
})();

/* =========================
   Phase 9.1.5 — Ledger Utilities (safe JSON parse + snapshot normalizer)
   Used by reorder page and any future ledger consumers.
   ========================= */

/**
 * Safe JSON parse — never throws; returns fallback on error.
 * @param {string} str - JSON string to parse
 * @param {*} fallback - value to return on parse failure (default: null)
 * @returns {*} parsed value or fallback
 */
const sharyJsonParseSafe = (str, fallback = null) => {
  if (typeof str !== "string" || !str.trim()) return fallback;
  try {
    return JSON.parse(str);
  } catch (_) {
    return fallback;
  }
};

/**
 * Normalize a ledger snapshot to a consistent shape (forward + backward compat).
 * Handles both new format (items/totals as objects) and legacy (items_json/totals_json as strings).
 * 
 * Expected server shape:
 * { id, reference, source, items:[], totals:{}, created_at, updated_at }
 * 
 * Legacy shape (if ever encountered):
 * { id, reference, items_json: "...", totals_json: "...", status, created_at }
 * 
 * @param {Object} snap - raw snapshot from ledger
 * @returns {Object} normalized snapshot
 */
const sharyNormalizeSnapshot = (snap) => {
  if (!snap || typeof snap !== "object") {
    return { id: null, reference: "", created_at: null, source: "", items: [], totals: {} };
  }

  // Normalize items: prefer items (array), else parse items_json, else []
  let items;
  if (Array.isArray(snap.items)) {
    items = snap.items;
  } else if (typeof snap.items_json === "string") {
    items = sharyJsonParseSafe(snap.items_json, []);
    if (!Array.isArray(items)) items = [];
  } else {
    items = [];
  }

  // Normalize totals: prefer totals (object), else parse totals_json, else {}
  let totals;
  if (snap.totals && typeof snap.totals === "object" && !Array.isArray(snap.totals)) {
    totals = snap.totals;
  } else if (typeof snap.totals_json === "string") {
    totals = sharyJsonParseSafe(snap.totals_json, {});
    if (!totals || typeof totals !== "object" || Array.isArray(totals)) totals = {};
  } else {
    totals = {};
  }

  return {
    id: snap.id || null,
    reference: snap.reference || "",
    created_at: snap.created_at || null,
    source: snap.source || "",
    items,
    totals,
    // Preserve status if present (may be used for display)
    status: snap.status || "pending",
  };
};

/* =========================
   Phase 9.2 — Reorder Page Initializer (Ledger-powered, limit 3 orders)
   Fetches last 3 orders from App Proxy ledger, falls back to localStorage.
   Renders order cards in Shary style with reorder CTA.
   
   Server response shape (authoritative):
   { ok: true, customer_id: "878...", snapshots: [ { id, reference, source, items:[], totals:{}, created_at, updated_at } ] }
   
   Validation rules (LOCKED):
   - If response.ok !== true → FAIL
   - Else if !Array.isArray(response.snapshots) → FAIL
   - Else → LEDGER IS VALID (even if snapshots is empty)
   ========================= */
const sharyInitReorderPage = async () => {
  const root = document.querySelector("[data-shary-reorder-root]");
  const listEl = document.getElementById("SharyReorderList");
  const emptyEl = document.querySelector("[data-shary-reorder-empty]");
  const loadingEl = document.querySelector("[data-shary-reorder-loading]");
  if (!root || !listEl) return;

  const body = document.body;
  const isAuthed = () => (parseInt(body?.getAttribute("data-shary-auth") || "0", 10) || 0) > 0;
  const debug = sharyDebugPanel("reorder", "REORDER PAGE");

  // Audit state (tracked for debug summary)
  const audit = {
    source: null,          // "LEDGER" | "FALLBACK"
    ledgerOk: null,        // true | false | null (not attempted)
    snapshotsRaw: 0,       // count before normalization
    snapshotsNorm: 0,      // count after normalization
    fallbackUsed: false,
    customerId: null,
    lastError: null,
    renderCount: 0,
  };

  const moneyFormat = (cents) => {
    if (cents == null) return "";
    const egp = (Number(cents) / 100).toFixed(2);
    return `${egp} ج.م`;
  };

  // HTML escape to prevent XSS
  const escapeHtml = (str) => {
    if (!str) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    try {
      const d = new Date(isoDate);
      return `${d.getDate().toString().padStart(2,"0")}/${(d.getMonth()+1).toString().padStart(2,"0")}/${d.getFullYear()}`;
    } catch (_) { return ""; }
  };

  const getStatusLabel = (status) => {
    const labels = { pending: "قيد المراجعة", confirmed: "تم التأكيد", cancelled: "ملغي" };
    return labels[status] || "قيد المراجعة";
  };

  const getStatusTone = (status) => {
    const tones = { pending: "warning", confirmed: "success", cancelled: "muted" };
    return tones[status] || "warning";
  };

  /**
   * Fetch ledger snapshots (last 3 orders).
   * Returns: Array of normalized snapshots (success, may be empty) | null (failure)
   * 
   * State machine:
   * A) Not authenticated → null (fail)
   * B) Network/fetch error → null (fail)
   * C) response.ok !== true → null (fail)
   * D) !Array.isArray(response.snapshots) → null (malformed fail)
   * E) response.snapshots is array → SUCCESS (even if empty)
   */
  const fetchLedgerSnapshots = async () => {
    debug.log("Init reorder", "pending");
    
    if (!isAuthed()) {
      debug.log("Not authenticated — ledger skipped", "fail", "not_authenticated");
      audit.lastError = "not_authenticated";
      return null;
    }

    const fetchUrl = "/apps/shary-ledger/list?limit=3";
    debug.log("Ledger fetch URL", "info", fetchUrl);

    try {
      const data = await sharyLedgerListStore.fetchList(fetchUrl);

      // Debug: log raw keys
      const rawKeys = data ? `ok=${data.ok}, snapshots.length=${Array.isArray(data.snapshots) ? data.snapshots.length : "N/A"}, customer_id=${data.customer_id || "N/A"}` : "null";
      debug.log("Ledger raw keys", "info", rawKeys);

      // Phase 2 validation: ONLY check ok + snapshots array
      if (!data || data.ok !== true) {
        debug.log("Ledger FAIL: ok !== true", "fail", JSON.stringify({ ok: data?.ok, error: data?.error }));
        audit.ledgerOk = false;
        audit.lastError = "ok_not_true";
        return null;
      }

      if (!Array.isArray(data.snapshots)) {
        debug.log("Ledger FAIL: snapshots not array", "fail", `typeof=${typeof data.snapshots}`);
        audit.ledgerOk = false;
        audit.lastError = "snapshots_not_array";
        return null;
      }

      // Ledger is VALID!
      audit.ledgerOk = true;
      audit.customerId = data.customer_id || null;
      audit.snapshotsRaw = data.snapshots.length;
      debug.log("Ledger VALID", "ok", `${data.snapshots.length} snapshots, customer_id=${data.customer_id || "—"}`);

      // Normalize each snapshot using Phase 9.1.5 normalizer
      const normalized = data.snapshots.map((snap, i) => {
        const norm = sharyNormalizeSnapshot(snap);
        debug.log(`Normalize snapshot #${i + 1}`, "info", `ref=${norm.reference || "—"}, items=${norm.items.length}, totals=${Object.keys(norm.totals).join(",") || "—"}`);
        return norm;
      });

      audit.snapshotsNorm = normalized.length;
      debug.log("localStorage skipped (ledger success)", "info");

      return normalized;

    } catch (err) {
      debug.log("Ledger FAIL: fetch error", "fail", err.message);
      audit.ledgerOk = false;
      audit.lastError = err.message;
      return null;
    }
  };

  // Render orders list with full items
  const renderOrders = (orders, isFallback = false) => {
    const source = isFallback ? "FALLBACK" : "LEDGER";
    audit.source = source;
    audit.fallbackUsed = isFallback;

    if (!orders || orders.length === 0) {
      listEl.innerHTML = "";
      if (emptyEl) emptyEl.hidden = false;
      const reason = isFallback ? "fallback empty" : (audit.ledgerOk ? "ledger returned 0 snapshots" : "ledger failed + no fallback");
      debug.log("Empty state shown", "info", `reason=${reason}`);
      audit.renderCount = 0;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    debug.log(`Render: ${source}, ${orders.length} orders`, "ok");
    audit.renderCount = orders.length;

    const html = orders.map((order) => {
      const ref = escapeHtml(order.reference || "—");
      const date = formatDate(order.created_at);
      const status = order.status || "pending";
      const statusLabel = getStatusLabel(status);
      const statusTone = getStatusTone(status);
      const items = order.items || [];
      const totalPrice = order.totals?.total_price;
      const itemCount = order.totals?.item_count || items.reduce((sum, it) => sum + (it.qty || it.quantity || 1), 0);

      // Base64-encode items for CTA button
      const itemsPayload = sharyBase64Encode(JSON.stringify(items));

      const itemsHtml = items.map((item) => {
        const title = escapeHtml(item.product_title || item.title || "منتج");
        const variant = item.variant_title && item.variant_title !== "Default Title" ? escapeHtml(item.variant_title) : "";
        const qty = item.qty || item.quantity || 1;
        const linePrice = item.price ? (item.price * qty) : (item.line_price || item.final_line_price || 0);
        const imgSrc = item.image_url || item.image || "";

        return `
          <li class="shary-reorder__item">
            ${imgSrc
              ? `<img class="shary-reorder__itemImg" src="${escapeHtml(imgSrc)}" alt="${title}" loading="lazy" width="40" height="40">`
              : `<span class="shary-reorder__itemPlaceholder" aria-hidden="true">📦</span>`
            }
            <div class="shary-reorder__itemInfo">
              <span class="shary-reorder__itemTitle">${title}</span>
              <span class="shary-reorder__itemMeta">
                ${variant ? `${variant} · ` : ""}× ${qty}${linePrice > 0 ? ` · ${moneyFormat(linePrice)}` : ""}
              </span>
            </div>
          </li>
        `;
      }).join("");

      return `
        <article class="shary-reorder__snapshot" data-order-ref="${ref}">
          <header class="shary-reorder__snapshotHead">
            <div class="shary-reorder__snapshotInfo">
              <div class="shary-reorder__snapshotRef">${ref}</div>
              ${date ? `<div class="shary-reorder__snapshotDate">${date}</div>` : ""}
            </div>
            <span class="shary-reorder__badge" data-tone="${statusTone}">${statusLabel}</span>
            ${isFallback ? `<span class="shary-reorder__badge" data-tone="muted" style="font-size:10px;">نسخة احتياطية</span>` : ""}
          </header>
          ${items.length > 0 ? `<ul class="shary-reorder__items">${itemsHtml}</ul>` : ""}
          <div class="shary-reorder__totals">
            <span class="shary-reorder__totalCount">${itemCount} ${itemCount === 1 ? "صنف" : itemCount === 2 ? "صنفان" : "أصناف"}</span>
            ${totalPrice ? `<span class="shary-reorder__totalPrice">${moneyFormat(totalPrice)}</span>` : ""}
          </div>
          <div class="shary-reorder__actions">
            <button type="button" class="shary-btn shary-btn--brand shary-reorder__cta" data-shary-reorder-items="${itemsPayload}">إعادة الطلب</button>
          </div>
        </article>
      `;
    }).join("");

    listEl.innerHTML = html;
  };

  // Fallback: convert localStorage items to pseudo-order format
  const getLocalStorageFallback = () => {
    debug.log("Fallback: reading localStorage", "pending");
    const now = Date.now();
    let localList = sharyReorderLoad();
    localList = sharyReorderPrune(localList, now);
    if (localList.length === 0) {
      debug.log("localStorage fallback empty", "info");
      return null;
    }
    
    // Group all items into a single "order"
    const items = localList.map(item => ({
      variant_id: item.variant_id,
      product_title: item.title,
      title: item.title,
      qty: 1,
      price: item.price,
      image: item.image,
    }));
    
    debug.log(`localStorage fallback: ${items.length} items`, "ok");
    return [{
      reference: "نسخة محلية",
      created_at: new Date().toISOString(),
      status: "pending",
      items: items,
      totals: {
        item_count: items.length,
        total_price: items.reduce((sum, it) => sum + (it.price || 0), 0),
      }
    }];
  };

  /**
   * Log audit summary to debug panel.
   */
  const logAuditSummary = () => {
    const auditSummary = {
      source: audit.source || "NONE",
      ledgerOk: audit.ledgerOk === null ? "N/A" : audit.ledgerOk,
      snapshots: audit.snapshotsNorm,
      fallbackUsed: audit.fallbackUsed,
      customerId: audit.customerId || null,
      lastError: audit.lastError || null,
    };

    const summary = [
      `SOURCE=${auditSummary.source}`,
      `LEDGER_OK=${auditSummary.ledgerOk}`,
      `SNAPSHOTS=${auditSummary.snapshots}`,
      `FALLBACK_USED=${auditSummary.fallbackUsed}`,
      auditSummary.customerId ? `CUSTOMER_ID=${auditSummary.customerId}` : null,
      auditSummary.lastError ? `LAST_ERROR=${auditSummary.lastError}` : null,
    ].filter(Boolean).join(" | ");

    debug.log("─── Audit Summary ───", "info", summary);

    // Soft telemetry hook (silent)
    try {
      window.dispatchEvent(new CustomEvent("shary:ledger-audit", { detail: auditSummary }));
    } catch (_) {}
  };

  /**
   * Self-check function (debug=1 only): verify DOM state after render.
   * Asserts:
   * - #SharyReorderList exists
   * - loading hidden
   * - empty shown iff zero orders
   * - rendered cards count === audit.renderCount
   * 
   * Runs 2 passes max (initial + 500ms delayed).
   */
  const runSelfCheck = (pass = 1) => {
    if (!sharyDebugEnabled()) return;
    
    const failures = [];
    
    // Check 1: SharyReorderList exists
    const list = document.getElementById("SharyReorderList");
    if (!list) failures.push("SharyReorderList not found");
    
    // Check 2: loading hidden
    const loading = document.querySelector("[data-shary-reorder-loading]");
    if (loading && !loading.hidden) failures.push("loading still visible");
    
    // Check 3: empty shown iff zero orders
    const empty = document.querySelector("[data-shary-reorder-empty]");
    if (audit.renderCount === 0) {
      if (empty && empty.hidden) failures.push("empty should be visible (renderCount=0)");
    } else {
      if (empty && !empty.hidden) failures.push("empty should be hidden (renderCount>0)");
    }
    
    // Check 4: rendered cards count
    const cards = list ? list.querySelectorAll(".shary-reorder__snapshot") : [];
    if (cards.length !== audit.renderCount) {
      failures.push(`cards mismatch: DOM=${cards.length}, expected=${audit.renderCount}`);
    }

    if (failures.length === 0) {
      debug.log(`Self-check pass #${pass}`, "ok", "All assertions passed");
    } else {
      debug.log(`Self-check pass #${pass} FAIL`, "fail", failures.join("; "));
    }

    // Run second pass after 500ms (once only)
    if (pass === 1) {
      setTimeout(() => runSelfCheck(2), 500);
    }
  };

  // Main initialization
  try {
    debug.log("Initializing reorder page", "pending");
    
    // State machine: Try ledger first
    const ledgerSnapshots = await fetchLedgerSnapshots();

    if (loadingEl) loadingEl.hidden = true;

    // RULE: If ledger succeeded (returned array), it is the single source of truth.
    // localStorage fallback is ONLY used when ledger request failed (returned null).
    // "snapshots is empty" is a SUCCESSFUL ledger call, not a failure.
    if (ledgerSnapshots !== null) {
      // Ledger succeeded — render result (may be empty)
      renderOrders(ledgerSnapshots, false);
    } else {
      // Ledger failed — fallback to localStorage (offline/error/auth only)
      debug.log("Ledger unavailable — fallback to localStorage", "warn");
      audit.fallbackUsed = true;
      const fallbackOrders = getLocalStorageFallback();
      if (fallbackOrders) {
        renderOrders(fallbackOrders, true);
      } else {
        renderOrders([], false);
      }
    }

    // Log audit summary
    logAuditSummary();

    // Run self-check (debug mode only, 2 passes max)
    runSelfCheck(1);

  } catch (err) {
    debug.log("Init error", "fail", err.message);
    audit.lastError = err.message;
    if (loadingEl) loadingEl.hidden = true;
    renderOrders([], false);
    logAuditSummary();
  }
};

// Initialize reorder page on DOMContentLoaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", sharyInitReorderPage);
} else {
  sharyInitReorderPage();
}

/* =========================
   Phase 9.2.2 — Account Recommendations (Last Orders + Fallback)
   Lazy-loads based on viewport, never blocks rendering
   ========================= */
(() => {
  try {
    const root = document.querySelector("[data-shary-account-reco]");
    if (!root) return;

    const grid = root.querySelector("[data-shary-account-reco-grid]");
    if (!grid) return;

    const body = document.body;
    const customerId = body?.getAttribute("data-shary-customer-id") || "0";
    const isApproved = () => (parseInt(body?.getAttribute("data-shary-auth") || "0", 10) || 0) > 0;

    if (!customerId || customerId === "0") {
      if (root.parentNode) root.parentNode.removeChild(root);
      return;
    }

    const fallbackHandle = (root.getAttribute("data-fallback-handle") || "").trim();
    const fallbackUrl = (root.getAttribute("data-fallback-url") || "").trim();
    const gateText = (body?.getAttribute("data-shary-gate-price") || "سجل لرؤية الأسعار").trim();

    let initStarted = false;
    let fallbackPromise = null;

    const escapeHtml = (str) => {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    const getItemKey = (item) => {
      return item.product_id || item.productId || item.handle || item.product_handle || item.url || item.title || "";
    };

    const pickLedgerItems = (snapshots) => {
      const picked = [];
      const seen = new Set();
      for (const snap of snapshots || []) {
        for (const item of snap.items || []) {
          const hasLink = item.url || item.product_url || item.handle || item.product_handle;
          if (!hasLink) continue;
          const key = String(getItemKey(item) || "");
          if (!key || seen.has(key)) continue;
          seen.add(key);
          picked.push(item);
          if (picked.length >= 3) return picked;
        }
      }
      return picked;
    };

    const getUnitPrice = (item) => {
      const qty = item.qty || item.quantity || 1;
      if (item.price != null) return item.price;
      if (item.final_price != null) return item.final_price;
      if (item.line_price != null && qty > 0) return Math.round(item.line_price / qty);
      if (item.final_line_price != null && qty > 0) return Math.round(item.final_line_price / qty);
      return null;
    };

    const buildCardHtml = (item) => {
      const title = escapeHtml(item.product_title || item.title || "منتج");
      const handle = item.handle || item.product_handle || "";
      const url = item.url || item.product_url || (handle ? `/products/${encodeURIComponent(handle)}` : "");
      const imgSrc = item.image_url || item.image || item.featured_image?.url || "";
      const priceCents = getUnitPrice(item);
      const showPrice = isApproved();
      const priceLabel = showPrice && priceCents != null
        ? sharyWaMoneyFormat(priceCents)
        : gateText;

      return `
        <article
          class="shary-card"
          data-shary-card
          data-product-id="${escapeHtml(item.product_id || "")}"
          data-variant-id="${escapeHtml(item.variant_id || "")}"
          data-available="1"
          data-title="${title}"
          data-pack=""
          data-url="${escapeHtml(url)}"
          data-img="${escapeHtml(imgSrc)}"
          data-price-cents="${priceCents != null ? escapeHtml(priceCents) : ""}"
        >
          <div class="shary-card__media-wrap">
            <a class="shary-card__media" href="${escapeHtml(url)}" aria-label="${title}">
              ${imgSrc
                ? `<img class="shary-card__img" src="${escapeHtml(imgSrc)}" alt="${title}" loading="lazy" width="600" height="600">`
                : `<div class="shary-card__img shary-card__img--empty" aria-hidden="true"></div>`
              }
              <div class="shary-card__overlay" aria-hidden="true"></div>
            </a>
          </div>
          <div class="shary-card__body">
            <a class="shary-card__title" href="${escapeHtml(url)}">${title}</a>
            <div class="shary-card__meta">
              <span class="shary-card__pack shary-card__pack--empty" aria-hidden="true">&nbsp;</span>
            </div>
            <div class="shary-card__price">
              <span>${escapeHtml(priceLabel)}</span>
            </div>
          </div>
        </article>
      `;
    };

    const fetchLedgerSnapshots = async () => {
      const fetchUrl = "/apps/shary-ledger/list?limit=3";
      try {
        const data = await sharyLedgerListStore.fetchList(fetchUrl);
        if (!data || data.ok !== true || !Array.isArray(data.snapshots)) return null;
        return data.snapshots.map((snap) => sharyNormalizeSnapshot(snap));
      } catch (_) {
        return null;
      }
    };

    const fetchFallbackCards = async () => {
      if (!fallbackHandle && !fallbackUrl) return [];
      if (fallbackPromise) return fallbackPromise;

      const baseUrl = fallbackUrl || `/collections/${encodeURIComponent(fallbackHandle)}`;
      const requestUrl = `${baseUrl}?sections=shary-listing`;

      fallbackPromise = fetch(requestUrl, { credentials: "same-origin" })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          const html = data && data["shary-listing"] ? data["shary-listing"] : "";
          if (!html) return [];
          const temp = document.createElement("div");
          temp.innerHTML = html;
          return Array.from(temp.querySelectorAll("[data-shary-card]")).slice(0, 3).map((card) => card.outerHTML);
        })
        .catch(() => []);

      return fallbackPromise;
    };

    const renderCards = (cardsHtml) => {
      if (!cardsHtml || cardsHtml.length === 0) {
        root.hidden = true;
        return;
      }
      grid.innerHTML = cardsHtml.join("");
      root.hidden = false;
    };

    const init = async () => {
      const snapshots = await fetchLedgerSnapshots();
      if (snapshots && snapshots.length > 0) {
        const items = pickLedgerItems(snapshots);
        if (items.length > 0) {
          renderCards(items.map(buildCardHtml));
          return;
        }
      }

      const fallbackCards = await fetchFallbackCards();
      if (fallbackCards.length > 0) {
        renderCards(fallbackCards);
        return;
      }

      root.hidden = true;
    };

    const start = () => {
      if (initStarted) return;
      initStarted = true;
      init();
    };

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          start();
        }
      }, { rootMargin: "200px 0px" });
      observer.observe(root);
    } else {
      requestAnimationFrame(start);
    }
  } catch (_) {}
})();

/* =========================
   Phase 9.2 — Manual QA Checklist
   ========================= 
   
   MANUAL QA CHECKLIST (run after any reorder changes):
   
   1) /pages/reorder?debug=1 while logged in
      - Expect "Ledger VALID" in debug panel
      - If snapshots exist, expect rendered order includes latest reference
      - Audit Summary shows SOURCE=LEDGER, LEDGER_OK=true
   
   2) Create snapshot via checkout flow
      - Complete a WhatsApp order through normal checkout
      - Refresh /pages/reorder?debug=1
      - New reference appears in list (most recent first)
   
   3) Ledger returns empty snapshots (new account)
      - Expect empty state with "لا توجد طلبات سابقة"
      - Debug shows "Empty state shown: reason=ledger returned 0 snapshots"
      - NO fallback used (FALLBACK_USED=false in summary)
   
   4) Unauthenticated user
      - Logout and visit /pages/reorder?debug=1
      - Expect fallback allowed
      - Debug shows "Not authenticated — ledger skipped" with "not_authenticated"
   
   5) Cross-device consistency
      - With ledger available: compare mobile vs desktop
      - Both should show identical results (no device storage dependency)
      - localStorage is NEVER read when ledger succeeds
   
   6) Self-check assertions (debug=1 only)
      - "Self-check pass #1" and "Self-check pass #2" should both show "ok"
      - If any assertion fails, investigate DOM state
   
   ========================= */

/* =========================
   Phase 9.2.1 — Global Reorder Button Handler
   Handles [data-shary-reorder-items] clicks from both:
   - Liquid-rendered metaobject snapshots
   - JS-rendered localStorage fallback
   Flow: Clear cart → Add items → Redirect to WhatsApp
   ========================= */
(() => {
  const root = document.querySelector("[data-shary-reorder-root]");
  if (!root) return;

  const body = document.body;
  const getWaPhone = () => (body?.getAttribute("data-shary-wa-phone") || "").replace(/[^\d]/g, "");

  const handleReorderClick = async (btn) => {
    if (!btn || btn.disabled) return;
    
    const itemsJson = btn.getAttribute("data-shary-reorder-items");
    if (!itemsJson) return;

    let items;
    try {
      // Use UTF-8 safe base64 decode (matches sharyBase64Encode used to create payload)
      items = JSON.parse(sharyBase64Decode(itemsJson));
    } catch (utf8Err) {
      try {
        // Fallback to URL-encoded JSON
        items = JSON.parse(decodeURIComponent(itemsJson));
      } catch (urlErr) {
        sharyLog("Reorder decode failed", { 
          utf8Error: utf8Err?.message || "sharyBase64Decode failed",
          urlError: urlErr?.message || "decodeURIComponent failed",
          dataLength: itemsJson?.length || 0
        });
        return;
      }
    }

    if (!items || items.length === 0) return;

    const waPhone = getWaPhone();
    if (!waPhone) {
      alert("رقم الواتساب غير مُعد. يرجى التواصل مع الدعم.");
      return;
    }

    btn.disabled = true;
    const origText = btn.textContent;
    btn.textContent = "جاري...";

    const debug = sharyDebugPanel("reorder-cta", "REORDER CTA");
    debug.log("Starting reorder flow", "pending", `${items.length} items`);

    try {
      // Step 1: Clear cart first
      debug.log("Clearing cart...", "pending");
      sharyCartStore.invalidate(); // Invalidate cache before clear
      await fetch("/cart/clear.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({}),
        credentials: "same-origin",
      });
      debug.log("Cart cleared (initial)", "ok");

      // Step 2: Add all items to cart
      debug.log("Adding items to cart...", "pending");
      let successCount = 0;
      for (const item of items) {
        const vid = item.variant_id || item.id;
        const qty = item.qty || item.quantity || 1;
        if (!vid) continue;
        
        try {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: String(vid), quantity: qty }),
          });
          if (res.ok) successCount++;
        } catch (_) {}
      }
      sharyCartStore.invalidate(); // Invalidate cache after adds
      debug.log(`Added ${successCount}/${items.length} items`, successCount > 0 ? "ok" : "fail");

      if (successCount === 0) {
        debug.log("No items added - aborting", "fail");
        btn.textContent = "خطأ - لم تتم الإضافة";
        setTimeout(() => {
          btn.textContent = origText;
          btn.disabled = false;
        }, 2000);
        return;
      }

      // Step 3: Build WhatsApp message using shared utility
      const message = sharyBuildCartCanonicalMessage({
        items,
        totals: null,
        meta: {}
      });
      if (!message) {
        debug.log("Message build failed", "fail");
        btn.textContent = "خطأ في إعداد الرسالة";
        setTimeout(() => {
          btn.textContent = origText;
          btn.disabled = false;
        }, 2000);
        return;
      }
      debug.log("Message built", "ok", `${message.length} chars`);

      // Step 4: Clear cart before WhatsApp redirect using reliable method
      debug.log("Final cart clear (keepalive)...", "pending");
      const clearResult = await sharyClearCartReliable();
      debug.log("Cart clear sent", "ok", `beacon:${clearResult.beaconSent}, fetch:${clearResult.fetchOk}`);

      // Step 5: Redirect to WhatsApp
      const waUrl = sharyBuildWhatsAppUrl(waPhone, message);
      if (!waUrl) {
        debug.log("WhatsApp URL invalid", "fail");
        btn.textContent = "خطأ في إعداد الواتساب";
        setTimeout(() => {
          btn.textContent = origText;
          btn.disabled = false;
        }, 2000);
        return;
      }
      debug.log("Redirecting to WhatsApp", "ok");
      window.location.href = waUrl;

    } catch (err) {
      debug.log("Reorder error", "fail", err.message);
      console.warn("[SHARY] Reorder error:", err);
      btn.textContent = "خطأ";
      setTimeout(() => {
        btn.textContent = origText;
        btn.disabled = false;
      }, 2000);
    }
  };

  // Delegate click handling to root (ensure only one handler)
  if (root.dataset.reorderBound === "1") return;
  root.dataset.reorderBound = "1";
  
  root.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-shary-reorder-items]");
    if (btn) {
      e.preventDefault();
      handleReorderClick(btn);
    }
  });
})();

/* =========================
   Phase 9.5 — B2B Apply return_to guard
   Uses sharyAuthCfg.pendingUrl (single source of truth)
   ========================= */
(() => {
  try {
    const section = document.querySelector(".shary-b2b-apply");
    if (!section) return;

    // Priority: data-return-to attr > sharyAuthCfg.pendingUrl > fallback
    const followUrl = (section.getAttribute("data-return-to") || "").trim() || sharyAuthCfg.pendingUrl || "/pages/registration-pending";
    const ensureReturnTo = (form) => {
      if (!(form instanceof HTMLFormElement)) return;
      let input = form.querySelector('input[name="return_to"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "return_to";
        form.appendChild(input);
      }
      input.value = followUrl;
    };

    const patchForms = (root) => {
      if (!(root instanceof HTMLElement)) return;
      root.querySelectorAll("form").forEach(ensureReturnTo);
    };

    patchForms(section);

    const observer = new MutationObserver((mutations) => {
      sharyPerf.count('observerTriggers');
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.tagName === "FORM") {
            ensureReturnTo(node);
          } else {
            patchForms(node);
          }
        });
      });
    });

    observer.observe(section, { childList: true, subtree: true });
  } catch (e) {}
})();

/* =========================
   Phase 10–12 — Hardening (soft guardrails only)
   ========================= */
(() => {
  try {
    // Performance: Only run promo word detection in debug mode (avoids expensive DOM text read)
    if (!window.SHARY_DEBUG) return;
    const promoWords = ["خصم", "لفترة", "عروض", "Sale", "Discount", "Limited time", "Flash"];
    const text = (document.body?.innerText || "").toLowerCase();
    if (promoWords.some(w => text.includes(String(w).toLowerCase()))) {
      console.warn("[SHARY V1] Promo noise detected (check content):", promoWords);
    }
  } catch (e) {}
})();

/* =========================
   Global Inline Search (Architecture 3)
   Owner: snippets/shary-global-search.liquid
   Features:
   - Opens inline search panel (not /search navigation)
   - Predictive search via /search/suggest.json
   - Price gating for guests
   - Debounced input, AbortController for race conditions
   - RTL, Arabic-only, accessible
   ========================= */
(() => {
  try {
    const root = document.querySelector("[data-shary-search]");
    if (!root) return;

    const body = document.body;
    if (!body) return;

    // Read settings from data attributes
    const searchLimit = parseInt(root.getAttribute("data-search-limit") || "8", 10) || 8;
    const searchMinChars = parseInt(root.getAttribute("data-search-min-chars") || "1", 10) || 1;
    // V1 Policy: searchFallback is intentionally ignored - /search navigation is banned
    const emptyHint = (root.getAttribute("data-search-empty-hint") || "اكتب للبحث عن المنتجات").trim();
    const noResultsText = (root.getAttribute("data-search-no-results") || "لا توجد نتائج").trim();
    const tryAgainText = (root.getAttribute("data-search-try-again") || "حاول مرة أخرى").trim();

    // Query elements
    const openBtn = root.querySelector("[data-shary-search-open]");
    const panel = root.querySelector("[data-shary-search-panel]");
    const backdrop = panel ? panel.querySelector("[data-shary-search-close]") : null;
    const closeBtn = panel ? panel.querySelector("button[data-shary-search-close]") : null;
    const input = panel ? panel.querySelector("[data-shary-search-input]") : null;
    const clearBtn = panel ? panel.querySelector("[data-shary-search-clear]") : null;
    const statusEl = panel ? panel.querySelector("[data-shary-search-status]") : null;
    const resultsEl = panel ? panel.querySelector("[data-shary-search-results]") : null;

    if (!openBtn || !panel || !input || !resultsEl) return;

    // Auth state for price gating
    const isAuthed = () => (parseInt(body.getAttribute("data-shary-auth") || "0", 10) || 0) > 0;
    const gateText = (body.getAttribute("data-shary-gate-price") || "سجل لرؤية الأسعار").trim();

    // AbortController for fetch race conditions
    let currentController = null;

    // Debounce timer
    let debounceTimer = null;
    const DEBOUNCE_MS = 200;

    // State
    let isOpen = false;

    // Focus trap cleanup and opener for Search Panel
    let searchTrapCleanup = null;
    let searchOpener = null;

    // ========================
    // PANEL OPEN/CLOSE
    // ========================

    const openPanel = () => {
      if (isOpen) return;
      isOpen = true;
      // Store opener for focus restore on close
      searchOpener = document.activeElement;

      panel.removeAttribute("hidden");
      panel.setAttribute("data-open", "true");
      openBtn.setAttribute("aria-expanded", "true");
      
      // A) Add scroll lock classes to both html and body
      document.documentElement.classList.add("is-shary-search-open");
      body.classList.add("is-shary-search-open");
      body.classList.add("shary-search-open");

      // C) Focus input reliably using requestAnimationFrame + setTimeout fallback
      // Both calls needed for iOS Safari compatibility where first focus may be ignored
      requestAnimationFrame(() => {
        input.focus();
        setTimeout(() => input.focus(), 0);
      });

      // Show hint if no results yet
      if (emptyHint && resultsEl.children.length === 0) {
        setStatus(emptyHint);
      }

      // Enable focus trap on the search panel
      searchTrapCleanup = sharyFocusTrap(panel);
    };

    const closePanel = () => {
      if (!isOpen) return;
      isOpen = false;

      panel.setAttribute("hidden", "");
      panel.removeAttribute("data-open");
      openBtn.setAttribute("aria-expanded", "false");
      
      // A) Remove scroll lock classes from both html and body
      document.documentElement.classList.remove("is-shary-search-open");
      body.classList.remove("is-shary-search-open");
      body.classList.remove("shary-search-open");

      // Abort any pending fetch
      if (currentController) {
        currentController.abort();
        currentController = null;
      }

      // Clear debounce
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }

      // Cleanup focus trap
      if (searchTrapCleanup) { searchTrapCleanup(); searchTrapCleanup = null; }
      // Restore focus to opener
      if (searchOpener && typeof searchOpener.focus === "function") {
        try { searchOpener.focus(); } catch (_) {}
      }
      searchOpener = null;
    };

    // ========================
    // STATUS & RESULTS HELPERS
    // ========================

    const setStatus = (msg, isError = false) => {
      if (!statusEl) return;
      if (!msg) {
        statusEl.innerHTML = "";
        return;
      }
      if (isError) {
        statusEl.innerHTML = `<span class="shary-search-panel__error">${escapeHtml(msg)}</span>`;
      } else {
        statusEl.innerHTML = `<span class="shary-search-panel__hint">${escapeHtml(msg)}</span>`;
      }
    };

    const setLoading = (loading) => {
      if (!statusEl) return;
      if (loading) {
        statusEl.innerHTML = `<span class="shary-search-panel__loading">جاري البحث...</span>`;
      }
    };

    const clearResults = () => {
      if (resultsEl) resultsEl.innerHTML = "";
    };

    // Performance: Reuse cached element for HTML escaping (avoids GC pressure)
    const escapeDiv = document.createElement("div");
    const escapeHtml = (str) => {
      escapeDiv.textContent = str;
      return escapeDiv.innerHTML;
    };

    // Format money (cents to EGP)
    const formatMoney = (cents) => {
      const value = (Number(cents || 0) / 100).toFixed(2);
      return `${value} EGP`;
    };

    // ========================
    // RENDER RESULTS
    // ========================

    const renderResults = (products) => {
      if (!resultsEl) return;

      if (!products || products.length === 0) {
        resultsEl.innerHTML = `
          <div class="shary-search-panel__empty">
            <div class="shary-search-panel__empty-icon">🔍</div>
            <div>${escapeHtml(noResultsText)}</div>
          </div>
        `;
        return;
      }

      const html = products.map((product) => {
        const title = escapeHtml(product.title || "");
        const url = product.url || "#";
        const imgSrc = product.featured_image?.url
          ? product.featured_image.url.replace(/(\.[^.]+)$/, "_160x160$1")
          : "";
        const price = product.price;

        // Price display: gated for guests
        let priceHtml = "";
        if (isAuthed() && typeof price === "number") {
          priceHtml = `<span class="shary-search-result__price">${formatMoney(price)}</span>`;
        } else if (!isAuthed()) {
          priceHtml = `<span class="shary-search-result__price-locked">${escapeHtml(gateText)}</span>`;
        }

        return `
          <a class="shary-search-result" href="${escapeHtml(url)}" role="listitem">
            ${imgSrc
              ? `<img class="shary-search-result__img" src="${escapeHtml(imgSrc)}" alt="" loading="lazy">`
              : `<span class="shary-search-result__img shary-search-result__img--empty"></span>`
            }
            <div class="shary-search-result__info">
              <span class="shary-search-result__title">${title}</span>
              ${priceHtml}
            </div>
          </a>
        `;
      }).join("");

      resultsEl.innerHTML = html;
    };

    // ========================
    // FETCH PREDICTIVE SEARCH
    // ========================

    const fetchSearch = async (query) => {
      // Abort previous request
      if (currentController) {
        currentController.abort();
      }

      currentController = new AbortController();
      const signal = currentController.signal;

      try {
        setLoading(true);
        setStatus("");

        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${searchLimit}`;

        const response = await fetch(url, {
          method: "GET",
          headers: { "Accept": "application/json" },
          credentials: "same-origin",
          signal,
        });

        if (!response.ok) {
          throw new Error("search_failed");
        }

        const data = await response.json();
        const products = data?.resources?.results?.products || [];

        renderResults(products);
        setStatus("");

      } catch (err) {
        if (err.name === "AbortError") {
          // Request was aborted, ignore
          return;
        }
        console.warn("[SHARY] Search error:", err);
        setStatus(tryAgainText, true);
      } finally {
        currentController = null;
      }
    };

    // ========================
    // INPUT HANDLER (DEBOUNCED)
    // ========================

    const handleInput = () => {
      const query = (input.value || "").trim();

      // Update clear button visibility
      if (clearBtn) {
        clearBtn.hidden = query.length === 0;
      }

      // Clear debounce timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }

      // Check min chars
      if (query.length < searchMinChars) {
        clearResults();
        if (emptyHint) {
          setStatus(emptyHint);
        } else {
          setStatus("");
        }
        return;
      }

      // Debounce fetch
      debounceTimer = setTimeout(() => {
        fetchSearch(query);
      }, DEBOUNCE_MS);
    };

    // ========================
    // EVENT HANDLERS
    // ========================

    // Open panel
    openBtn.addEventListener("click", (e) => {
      e.preventDefault();
      openPanel();
    });

    // Close via backdrop
    if (backdrop) {
      backdrop.addEventListener("click", (e) => {
        e.preventDefault();
        closePanel();
      });
    }

    // Close via button
    if (closeBtn) {
      closeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        closePanel();
      });
    }

    // Close via ESC key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        closePanel();
      }
    });

    // Input change
    input.addEventListener("input", handleInput);

    // Clear button
    if (clearBtn) {
      clearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        input.value = "";
        clearBtn.hidden = true;
        clearResults();
        if (emptyHint) {
          setStatus(emptyHint);
        } else {
          setStatus("");
        }
        input.focus();
      });
    }

    // Form submit / Enter key behavior
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();

        const query = (input.value || "").trim();
        if (!query) return;

        // V1 Policy: Never navigate to /search. Keep overlay open.
        // If results exist, user can click a result.
        // If no results, show status message (already handled by fetchSearch).
      }
    });

    // Prevent scroll on panel when body is locked
    panel.addEventListener("touchmove", (e) => {
      // Allow scroll inside results container
      const target = e.target;
      if (target && (target.closest(".shary-search-panel__results") || target.closest(".shary-search-panel__content"))) {
        return;
      }
      e.preventDefault();
    }, { passive: false });

  } catch (e) {
    console.warn("[SHARY] Global Search error:", e);
  }
})();

/* =========================
   Compare Slider — Neuraflake-style Comparable Cards
   Section: sections/shary-compare-slider.liquid
   Features:
   - Horizontal swipeable cards (CSS scroll-snap)
   - Autoplay sliding (configurable)
   - Manual swipe/drag support
   - Arrow navigation + pagination dots
   - Pause on interaction
   - Tab visibility handling
   - Multiple instances support
   - Animation speed control (anim_ms)
   - Active card stacking (is-active, is-near classes)
   ========================= */
(() => {
  try {
    const initCompareSlider = (root) => {
      if (!root) return;
      if (root.hasAttribute("data-compare-initialized")) return;
      root.setAttribute("data-compare-initialized", "true");

      const track = root.querySelector("[data-compare-track]");
      const slides = root.querySelectorAll("[data-compare-slide]");
      const prevBtn = root.querySelector("[data-compare-prev]");
      const nextBtn = root.querySelector("[data-compare-next]");
      const dots = root.querySelectorAll("[data-compare-dot]");

      if (!track || slides.length === 0) return;

      // Settings from data attributes
      const autoplayEnabled = root.getAttribute("data-autoplay") === "true";
      const autoplayInterval = parseInt(root.getAttribute("data-autoplay-interval") || "4000", 10) || 4000;
      const pauseOnInteraction = root.getAttribute("data-pause-on-interaction") !== "false";
      const pauseDuration = parseInt(root.getAttribute("data-pause-duration") || "5000", 10) || 5000;
      const pauseOnHover = root.getAttribute("data-pause-on-hover") !== "false";
      const animMs = parseInt(root.getAttribute("data-anim-ms") || "600", 10) || 600;

      // State
      let currentIndex = 0;
      let autoplayTimer = null;
      let pauseTimer = null;
      let isPaused = false;
      let isTabVisible = true;
      let isHovering = false;
      let scrollRafId = null;

      // Reduced motion check
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // RTL check
      const isRtl = document.documentElement.getAttribute("dir") === "rtl";

      // Custom smooth scroll function with configurable duration
      const animateScrollTo = (element, targetLeft, duration) => {
        if (prefersReducedMotion || duration <= 0) {
          element.scrollLeft = targetLeft;
          return;
        }
        const startLeft = element.scrollLeft;
        const distance = targetLeft - startLeft;
        const startTime = performance.now();

        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

        const step = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          element.scrollLeft = startLeft + distance * easeOutCubic(progress);
          if (progress < 1) {
            requestAnimationFrame(step);
          }
        };

        requestAnimationFrame(step);
      };

      // Get slide position for scrolling (offsetLeft-based for RTL safety)
      const getSlideTargetScroll = (slideIndex) => {
        const slide = slides[slideIndex];
        if (!slide) return 0;
        const trackRect = track.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const trackCenter = trackRect.width / 2;
        const slideCenter = slideRect.width / 2;
        // Calculate offset to center the slide
        const slideOffsetInTrack = slide.offsetLeft - track.scrollLeft + (slideRect.left - trackRect.left);
        return track.scrollLeft + (slideRect.left - trackRect.left) + slideCenter - trackCenter;
      };

      // Calculate current index from scroll position
      const getCurrentIndexFromScroll = () => {
        const trackRect = track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;
        
        slides.forEach((slide, i) => {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(slideCenter - trackCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = i;
          }
        });
        
        return closestIndex;
      };

      // Update active dot
      const updateDots = (index) => {
        dots.forEach((dot, i) => {
          const isActive = i === index;
          dot.classList.toggle("is-active", isActive);
          dot.setAttribute("aria-selected", isActive ? "true" : "false");
        });
      };

      // Update arrow states
      const updateArrows = (index) => {
        if (prevBtn) {
          prevBtn.disabled = index <= 0;
        }
        if (nextBtn) {
          nextBtn.disabled = index >= slides.length - 1;
        }
      };

      // Update active/near slide classes
      const updateSlideClasses = (index) => {
        slides.forEach((slide, i) => {
          slide.classList.remove("is-active", "is-near");
          if (i === index) {
            slide.classList.add("is-active");
          } else if (i === index - 1 || i === index + 1) {
            slide.classList.add("is-near");
          }
        });
      };

      // Scroll to specific slide
      const goToSlide = (index, useAnimation = true) => {
        const targetIndex = Math.max(0, Math.min(index, slides.length - 1));
        const targetSlide = slides[targetIndex];
        
        if (targetSlide) {
          // Calculate target scroll position to center the slide
          const trackRect = track.getBoundingClientRect();
          const slideRect = targetSlide.getBoundingClientRect();
          const currentScrollLeft = track.scrollLeft;
          const slideLeftInTrack = slideRect.left - trackRect.left + currentScrollLeft;
          const targetScrollLeft = slideLeftInTrack - (trackRect.width / 2) + (slideRect.width / 2);

          if (useAnimation && !prefersReducedMotion) {
            animateScrollTo(track, targetScrollLeft, animMs);
          } else {
            track.scrollLeft = targetScrollLeft;
          }
        }

        currentIndex = targetIndex;
        updateDots(targetIndex);
        updateArrows(targetIndex);
        updateSlideClasses(targetIndex);
      };

      // Go to previous slide
      const goPrev = () => {
        goToSlide(currentIndex - 1);
      };

      // Go to next slide (with loop)
      const goNext = () => {
        if (currentIndex >= slides.length - 1) {
          goToSlide(0);
        } else {
          goToSlide(currentIndex + 1);
        }
      };

      // Autoplay logic
      const startAutoplay = () => {
        if (!autoplayEnabled || isPaused || !isTabVisible || isHovering) return;
        stopAutoplay();
        autoplayTimer = setInterval(() => {
          if (!isPaused && isTabVisible) {
            goNext();
          }
        }, autoplayInterval);
      };

      const stopAutoplay = () => {
        if (autoplayTimer) {
          clearInterval(autoplayTimer);
          autoplayTimer = null;
        }
      };

      const pauseAutoplay = () => {
        if (!pauseOnInteraction) return;
        isPaused = true;
        stopAutoplay();

        // Clear any existing pause timer
        if (pauseTimer) {
          clearTimeout(pauseTimer);
        }

        // Resume after pause duration
        pauseTimer = setTimeout(() => {
          isPaused = false;
          startAutoplay();
        }, pauseDuration);
      };

      // Handle user interaction (pauses autoplay)
      const handleInteraction = () => {
        pauseAutoplay();
      };

      const handleHoverStart = () => {
        if (!pauseOnHover) return;
        isHovering = true;
        stopAutoplay();
      };

      const handleHoverEnd = () => {
        if (!pauseOnHover) return;
        isHovering = false;
        startAutoplay();
      };

      // Arrow click handlers
      if (prevBtn) {
        prevBtn.addEventListener("click", () => {
          handleInteraction();
          goPrev();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener("click", () => {
          handleInteraction();
          goNext();
        });
      }

      // Dot click handlers
      dots.forEach((dot) => {
        dot.addEventListener("click", () => {
          const index = parseInt(dot.getAttribute("data-compare-dot") || "0", 10);
          handleInteraction();
          goToSlide(index);
        });
      });

      // Track scroll event (for swipe/drag detection) - throttled via rAF
      const handleScroll = () => {
        if (scrollRafId) return;
        scrollRafId = requestAnimationFrame(() => {
          scrollRafId = null;
          const newIndex = getCurrentIndexFromScroll();
          if (newIndex !== currentIndex) {
            currentIndex = newIndex;
            updateDots(newIndex);
            updateArrows(newIndex);
            updateSlideClasses(newIndex);
          }
        });
      };
      track.addEventListener("scroll", handleScroll, { passive: true });

      // Hover pause handling
      root.addEventListener("mouseenter", handleHoverStart);
      root.addEventListener("mouseleave", handleHoverEnd);

      // Touch/mouse interaction detection (for pausing autoplay)
      track.addEventListener("touchstart", handleInteraction, { passive: true });
      track.addEventListener("mousedown", handleInteraction, { passive: true });

      // Tab visibility handling
      document.addEventListener("visibilitychange", () => {
        isTabVisible = !document.hidden;
        if (isTabVisible) {
          startAutoplay();
        } else {
          stopAutoplay();
        }
      });

      // Keyboard navigation
      root.addEventListener("keydown", (e) => {
        if (e.key === "ArrowLeft") {
          handleInteraction();
          isRtl ? goNext() : goPrev();
        } else if (e.key === "ArrowRight") {
          handleInteraction();
          isRtl ? goPrev() : goNext();
        }
      });

      // Initial state
      updateDots(0);
      updateArrows(0);
      updateSlideClasses(0);
      startAutoplay();

      // Cleanup on section unload (for Shopify theme editor)
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener("shopify:section:unload", (e) => {
          // Check if the unloaded section contains or is the root element
          if (e.target === root || (e.target && e.target.contains && e.target.contains(root))) {
            stopAutoplay();
            if (pauseTimer) clearTimeout(pauseTimer);
            if (scrollRafId) cancelAnimationFrame(scrollRafId);
          }
        });
      }
    };

    // Initialize all compare sliders on page
    const initAll = () => {
      document.querySelectorAll("[data-shary-compare]").forEach(initCompareSlider);
    };

    // Run on DOM ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initAll);
    } else {
      initAll();
    }

    // Re-initialize on Shopify section events (theme editor)
    document.addEventListener("shopify:section:load", (e) => {
      const section = e.target;
      if (section) {
        const slider = section.querySelector("[data-shary-compare]");
        if (slider) {
          slider.removeAttribute("data-compare-initialized");
          initCompareSlider(slider);
        }
      }
    });

  } catch (e) {
    console.warn("[SHARY] Compare Slider error:", e);
  }
})();

/* -------------------------
   Phase 10.8 — Register note builder
   Saves B2B fields into customer note at registration.
   ------------------------- */
(() => {
  try {
    const formArea = document.querySelector("[data-shary-register-form]");
    if (!formArea) return;

    const form = formArea.closest("form");
    const noteInput = formArea.querySelector("[data-shary-note-target]");
    if (!form || !noteInput) return;

    const labels = {
      store_name: "اسم المتجر",
      phone: "رقم الجوال",
      area: "المنطقة",
      address: "العنوان",
      commercial_register: "السجل التجاري",
      tax_id: "الرقم الضريبي",
    };

    const getValue = (key) => {
      const el = formArea.querySelector(`[data-shary-note-src="${key}"]`);
      return (el && typeof el.value === "string" ? el.value.trim() : "");
    };

    const buildNote = () => {
      const parts = [];
      parts.push(`${labels.store_name}: ${getValue("store_name")}`);
      parts.push(`${labels.phone}: ${getValue("phone")}`);
      parts.push(`${labels.area}: ${getValue("area")}`);
      parts.push(`${labels.address}: ${getValue("address")}`);

      const cr = getValue("commercial_register") || "-";
      const tax = getValue("tax_id") || "-";
      parts.push(`${labels.commercial_register}: ${cr}`);
      parts.push(`${labels.tax_id}: ${tax}`);

      noteInput.value = parts.join("\n");
    };

    form.addEventListener("input", buildNote);
    form.addEventListener("submit", buildNote);
    buildNote();
  } catch (e) {
    console.warn("[SHARY] Register note builder error", e);
  }
})();

/* -------------------------
   Phase 10.9 — Cart Clear Re-verification
   After WhatsApp redirect, user may return to find cart not cleared.
   This phase re-verifies cart state and clears if needed.
   ------------------------- */
(() => {
  try {
    const CART_CLEARED_FLAG = "shary_cart_cleared_v1";
    
    const reVerifyCartClear = async () => {
      const flag = sharyStorage.session.get(CART_CLEARED_FLAG);
      if (!flag) return;
      
      // Clear flag immediately to prevent infinite loops
      sharyStorage.session.remove(CART_CLEARED_FLAG);
      
      console.log("[SHARY] Re-verifying cart clear...");
      
      try {
        // Force fresh cart fetch for re-verification
        sharyCartStore.invalidate();
        const cart = await sharyCartStore.fetchCart(true);
        
        if (!cart) return;
        
        if (cart.items && cart.items.length > 0) {
          console.log("[SHARY] Cart still has items, re-clearing...");
          
          // Cart wasn't cleared properly - clear it now
          await fetch("/cart/clear.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({}),
            credentials: "same-origin",
          });
          
          // Invalidate cache after clear
          sharyCartStore.invalidate();
          
          // Update UI badges
          document.querySelectorAll("[data-shary-cart-badge]").forEach((badge) => {
            badge.textContent = "0";
            badge.hidden = true;
          });
          
          // Update cart total
          const totalEl = document.querySelector("[data-shary-cart-total]");
          if (totalEl) {
            totalEl.textContent = "0.00 ج.م";
            totalEl.setAttribute("data-subtotal-cents", "0");
          }
          
          // Dispatch event for other listeners
          window.dispatchEvent(new Event("shary:cart-updated"));
          
          console.log("[SHARY] Cart re-cleared successfully");
        } else {
          console.log("[SHARY] Cart already empty");
        }
      } catch (err) {
        console.warn("[SHARY] Cart re-verification error:", err);
      }
    };
    
    // Run on page load
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", reVerifyCartClear);
    } else {
      reVerifyCartClear();
    }
  } catch (e) {
    console.warn("[SHARY] Cart clear re-verification error:", e);
  }
})();

console.log("[Shary] shary-v1.js loaded");
